<!--app\javascript\dashboard\routes\dashboard\bulkmessage\pages\BulkMessageNew.vue-->

<script setup>
// Import all the same logic from the original BulkMessage.vue
// (All the imports, composables, state, and methods remain the same)
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { debug } from 'shared/helpers/debugHelper';
import InboxMetaAPI from 'dashboard/api/inboxMeta';

// Composables
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useBroadcastHelpers } from '../composables/useBroadcastHelpers';
import useCsvImport from '../utils/useCsvImport';

// Components
import RecipientList from '../components/RecipientList.vue';
import MessageComposer from '../components/MessageComposer.vue';
import MessageTemplate from '../components/MessageTemplate.vue';
import MessagePreview from '../components/MessagePreview.vue';
import IntervalControl from '../components/IntervalControl.vue';
import BroadcastProgressOverlay from '../components/BroadcastProgressOverlay.vue';
import ToastFix from 'dashboard/components-next/toast/ToastFix.vue';
import MobileTemplateValidationModal from '../components/modals/MobileTemplateValidationModal.vue';
import BroadcastConfirmationModal from '../components/modals/BroadcastConfirmationModal.vue';
import EmptyValuesValidationModal from '../components/modals/EmptyValuesValidationModal.vue';
import BroadcastScheduleModal from '../components/modals/BroadcastScheduleModal.vue';
import InboxSelectionModal from '../components/modals/InboxSelectionModal.vue';
import InvalidNumbersModal from '../components/modals/InvalidNumbersModal.vue';

// API
import ScheduledBroadcastsAPI from 'dashboard/api/scheduledBroadcasts';

const store = useStore();
const router = useRouter();
const route = useRoute();

// Initialize CSV import composable
const { mapping: csvMapping } = useCsvImport();

// Computed property to get additional columns for template variables
const additionalTemplateColumns = computed(() => {
  if (
    !csvMapping.additional_columns ||
    !Array.isArray(csvMapping.additional_columns)
  ) {
    return [];
  }
  return csvMapping.additional_columns;
});

// useAlert is called directly, not assigned to a variable

const pagination = ref({
  pageIndex: 0,
  pageSize: 25,
});

// State
const currentStep = ref(1);
const recipients = ref([]);
const selectedRecipientIds = ref([]);
const selectedInboxes = ref([]);
const message = ref('');
const attachments = ref([]);
const messagePreview = ref(false);
const isUploading = ref(false);
const isSubmitting = ref(false);
const selectedTemplate = ref(null);
const isUsingTemplate = ref(false);
const messageTemplateRef = ref(null);
const currentBroadcastId = ref(null);
const additionalColumns = ref([]); // Store additional CSV columns for table display

const inboxes = ref([]);
const loading = ref(false);
const error = ref(null);
const pendingInboxIds = ref([]); // Store inbox IDs to select after inboxes are loaded

const selectedDDI = ref('+55');
const userManuallySetGlobalInbox = ref(false); // Track manual inbox selection

const showProgressOverlay = ref(false);
const isCancelling = ref(false);
const showMobileValidationModal = ref(false);
const showEmptyValuesModal = ref(false);
const emptyFieldsValidationData = ref({});

// Store last contacts API response for pagination metadata
const lastContactsResponse = ref(null);
const mobileParameterErrors = ref({});
const showRiskConfirmationModal = ref(false);
const messageVariations = ref([]);
const messageComposerRef = ref(null);
// Store the full variations state to preserve when switching steps
const messageComposerVariationsState = ref({
  generatedVariations: [],
  selectedVariations: [],
});
const sendingProgress = ref({
  broadcastId: null,
  totalRecipients: 0,
  currentProgress: 0,
  successCount: 0,
  failedCount: 0,
  errors: [],
  isCancelled: false,
  // Network error retry state
  isWaitingNetworkRetry: false,
  networkRetryAttempt: 0,
  networkRetryCountdown: 0,
  connectivityStatus: null, // 'internet_down' | 'server_down' | 'ok' | null
});

const accountId = ref(null);
const showBroadcastConfirmationModal = ref(false);
const selectedBroadcastLabels = ref([]);
const selectedBroadcastLabelsToRemove = ref([]);
const showScheduleModal = ref(false);
const showInboxSelectionModal = ref(false);
const showInvalidNumbersModal = ref(false);
const invalidRecipients = ref([]); // Armazena destinatários com números inválidos separados
const isValidatingNumbers = ref(false); // Estado de loading para re-validação
const invalidNumbersModalRef = ref(null); // Ref para o modal de números inválidos

const minDelay = ref(15);
const maxDelay = ref(60);

// Drag and drop state for full-page overlay
const isDraggingFile = ref(false);
const documentDragCounter = ref(0);

const batchStartTimes = ref([]);
const averageBatchTime = ref(8000);
const isProcessingBatch = ref(false);
const currentBatchIndex = ref(0);
const totalBatches = ref(0);

// Inbox dropdown state
const activeInboxDropdown = ref(null); // recipient ID whose dropdown is open
const dropdownPosition = ref({ top: 0, left: 0, bottom: undefined });

// All the computed properties, methods, and lifecycle hooks from the original component
// (Copy all the same logic from BulkMessage.vue, but remove the tab switching logic)

// Computed property for formatted inbox metadata from Vuex store
const formattedInboxMetadata = computed(
  () => store.getters['inboxMetadata/getFormattedInboxes']
);

const hasRecipients = computed(() => recipients.value.length > 0);
const hasMessage = computed(
  () =>
    message.value.trim().length > 0 ||
    selectedTemplate.value !== null ||
    attachments.value.length > 0
);

const canScheduleBroadcast = computed(() => {
  return hasRecipients.value && hasMessage.value;
});
// Computed property to get unique inboxes actually in use by recipients
const uniqueInboxesInUse = computed(() => {
  const inboxSet = new Set();

  // Add inboxes from recipients with specific inbox selections
  recipients.value.forEach(recipient => {
    if (
      recipient.selectedInbox !== null &&
      recipient.selectedInbox !== undefined
    ) {
      inboxSet.add(recipient.selectedInbox);
    }
  });

  // If no individual selections but global inboxes are selected (for random distribution)
  if (inboxSet.size === 0 && selectedInboxes.value.length > 0) {
    selectedInboxes.value.forEach(inboxId => inboxSet.add(inboxId));
  }

  return Array.from(inboxSet);
});

// Computed property to get only connected inboxes for the selection modal
const connectedInboxes = computed(() => {
  return inboxes.value.filter(inbox => inbox.isConnected);
});

const canProceedToNext = computed(() => {
  if (currentStep.value === 1) {
    if (!hasRecipients.value) return false;

    // Check for mixed inbox types - cannot proceed if mixing official and unofficial
    if (hasMixedInboxTypes.value) return false;

    const hasGlobalInbox = selectedInboxes.value.length > 0;
    const hasIndividualInboxes = recipients.value.some(
      r => r.selectedInbox !== null && r.selectedInbox !== undefined
    );
    return hasGlobalInbox || hasIndividualInboxes;
  }
  if (currentStep.value === 2) {
    return hasMessage.value;
  }
  return true;
});

const canSend = computed(() => {
  if (!hasRecipients.value) return false;

  if (whatsappProviders.value.official && !whatsappProviders.value.unofficial) {
    return selectedTemplate.value !== null;
  }

  // Enable sending if there's either a message OR attachments
  return message.value.trim().length > 0 || attachments.value.length > 0;
});

// Tooltip message for "Próximo" button
const nextButtonTooltip = computed(() => {
  if (currentStep.value === 1) {
    if (!hasRecipients.value) {
      return 'Adicione contatos para continuar';
    }

    // Check for mixed inbox types
    if (hasMixedInboxTypes.value) {
      return 'Todos os contatos devem usar o mesmo tipo de inbox.';
    }

    const hasGlobalInbox = selectedInboxes.value.length > 0;
    const hasIndividualInboxes = recipients.value.some(
      r => r.selectedInbox !== null && r.selectedInbox !== undefined
    );
    if (!hasGlobalInbox && !hasIndividualInboxes) {
      return 'Selecione um inbox';
    }
  }
  return '';
});

// Tooltip message for "Agendar disparo" button
const scheduleButtonTooltip = computed(() => {
  if (!canScheduleBroadcast.value) {
    return 'Adicione destinatários e conteúdo para agendar o disparo';
  }
  return '';
});

const currentUser = useMapGetter('getCurrentUser');
const allContactLabels = useMapGetter('labels/getLabels');
const contacts = useMapGetter('contacts/getContacts');

const whatsappInboxes = computed(() => {
  const cloudInboxes = inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'whatsapp' &&
      inbox.provider === 'whatsapp_cloud_api'
  );

  const unofficialInboxes = inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'whatsapp' &&
      inbox.provider !== 'whatsapp_cloud_api'
  );

  return { cloudInboxes, unofficialInboxes };
});

const whatsappProviders = computed(() => {
  // Use actual inboxes in use by recipients, not just selectedInboxes
  const activeInboxes = uniqueInboxesInUse.value;

  if (!activeInboxes.length) return { official: false, unofficial: false };

  const { cloudInboxes, unofficialInboxes } = whatsappInboxes.value;

  const hasOfficial = activeInboxes.some(inboxId =>
    cloudInboxes.some(inbox => inbox.id === inboxId)
  );

  const hasUnofficial = activeInboxes.some(inboxId =>
    unofficialInboxes.some(inbox => inbox.id === inboxId)
  );

  return { official: hasOfficial, unofficial: hasUnofficial };
});

const isUsingWhatsappCloud = computed(() => whatsappProviders.value.official);

const showTemplateSelection = computed(() => isUsingWhatsappCloud.value);

// Check if using ONLY official API (not mixed with unofficial)
const isUsingOfficialApi = computed(() => {
  return (
    whatsappProviders.value.official && !whatsappProviders.value.unofficial
  );
});

// Detect if recipients have mixed inbox types (both official and unofficial)
const hasMixedInboxTypes = computed(() => {
  if (!recipients.value || recipients.value.length === 0) return false;

  let hasOfficial = false;
  let hasUnofficial = false;

  // Check each recipient's inbox type
  recipients.value.forEach(recipient => {
    if (!recipient.selectedInbox) return;

    const inbox = inboxes.value.find(i => i.id === recipient.selectedInbox);
    if (!inbox) return;

    // Check if inbox is official
    if (
      inbox.integration_type === 'whatsapp_cloud' ||
      inbox.integration_type === 'official' ||
      inbox.provider === 'whatsapp_cloud_api'
    ) {
      hasOfficial = true;
    }
    // Check if inbox is unofficial/evolution/messenger
    else if (
      inbox.integration_type === 'evolution' ||
      inbox.integration_type === 'messenger' ||
      inbox.channel_type === 'messenger' ||
      (inbox.channel_type === 'whatsapp' &&
        inbox.provider !== 'whatsapp_cloud_api')
    ) {
      hasUnofficial = true;
    }
  });

  // Return true if we have BOTH types
  return hasOfficial && hasUnofficial;
});

// Check if any inbox being used is Messenger type
const hasMessengerInbox = computed(() => {
  const activeInboxIds = uniqueInboxesInUse.value;

  return activeInboxIds.some(inboxId => {
    const inbox = inboxes.value.find(i => i.id === inboxId);
    if (!inbox) return false;

    return (
      inbox.integration_type === 'messenger' ||
      inbox.channel_type === 'messenger'
    );
  });
});

const hasRiskyIntervals = computed(() => {
  // Official API: No ban risk, skip warning modal
  if (isUsingOfficialApi.value) {
    return false;
  }
  // Unofficial API: Keep existing logic unchanged
  return minDelay.value < 15 || maxDelay.value < 60;
});

// Check for disconnected inboxes and count affected recipients
const disconnectedInboxWarning = computed(() => {
  // Get all unique inbox IDs being used by recipients
  const inboxUsage = new Map();

  recipients.value.forEach(recipient => {
    const inboxId = recipient.selectedInbox;
    if (inboxId) {
      if (!inboxUsage.has(inboxId)) {
        inboxUsage.set(inboxId, { count: 0, inbox: null });
      }
      inboxUsage.get(inboxId).count++;
    }
  });

  // Check which inboxes are disconnected
  const warnings = [];

  inboxUsage.forEach((usage, inboxId) => {
    const inbox = inboxes.value.find(i => i.id === inboxId);
    if (inbox && !inbox.isConnected) {
      warnings.push({
        inboxId,
        inboxName: inbox.name || inbox.inbox_name || `Inbox ${inboxId}`,
        recipientCount: usage.count,
        integrationType: inbox.integration_type,
      });
    }
  });

  return warnings;
});

// Grouped inbox options for dropdown
const groupedInboxOptions = computed(() => {
  const result = [];
  const inboxesList = inboxes.value || [];

  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const messengerInboxes = [];
  const otherChannels = [];

  inboxesList.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

    if (
      inbox.integration_type === 'whatsapp_cloud' ||
      inbox.integration_type === 'official' ||
      name.includes('api oficial') ||
      name.includes('cloud') ||
      name.includes('official')
    ) {
      whatsappOfficial.push({ ...inbox, integration_type: 'official' });
    } else if (
      inbox.integration_type === 'evolution' ||
      name.includes('evolution') ||
      name.includes('unofficial')
    ) {
      whatsappEvolution.push({ ...inbox, integration_type: 'evolution' });
    } else if (
      inbox.integration_type === 'messenger' ||
      inbox.channel_type === 'messenger' ||
      name.includes('messenger') ||
      name.includes('facebook')
    ) {
      messengerInboxes.push({ ...inbox, integration_type: 'messenger' });
    } else {
      otherChannels.push(inbox);
    }
  });

  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
    result.push(...whatsappEvolution);
  }

  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
    result.push(...whatsappOfficial);
  }

  if (messengerInboxes.length > 0) {
    result.push({ type: 'header', label: 'MESSENGER' });
    result.push(...messengerInboxes);
  }

  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'OUTROS CANAIS' });
    result.push(...otherChannels);
  }

  return result;
});

// Helper function to determine inbox type (official, unofficial, messenger, or other)
const getInboxType = inboxId => {
  const inbox = inboxes.value.find(i => i.id === inboxId);
  if (!inbox) return null;

  const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

  // Check for Messenger first
  if (
    inbox.integration_type === 'messenger' ||
    inbox.integration_type === 'facebook_messenger' ||
    inbox.channel_type === 'Channel::FacebookPage' ||
    name.includes('messenger') ||
    name.includes('facebook')
  ) {
    return 'messenger';
  }

  // Check for Official WhatsApp
  if (
    inbox.integration_type === 'whatsapp_cloud' ||
    inbox.integration_type === 'official' ||
    inbox.provider === 'whatsapp_cloud_api'
  ) {
    return 'official';
  }

  // Check for Unofficial WhatsApp
  if (
    inbox.integration_type === 'evolution' ||
    (inbox.channel_type === 'whatsapp' &&
      inbox.provider !== 'whatsapp_cloud_api')
  ) {
    return 'unofficial';
  }

  return 'other';
};

// Filtered grouped inbox options for dropdown - filters based on current recipient's inbox type
const filteredGroupedInboxOptionsForDropdown = computed(() => {
  // If no active dropdown, return all options
  if (!activeInboxDropdown.value) return groupedInboxOptions.value;

  // Find the current recipient being edited
  const currentRecipient = recipients.value.find(
    r => r.id === activeInboxDropdown.value
  );
  if (!currentRecipient || !currentRecipient.selectedInbox) {
    return groupedInboxOptions.value;
  }

  // Get the current recipient's inbox type
  const currentInboxType = getInboxType(currentRecipient.selectedInbox);

  // If unofficial, filter out official and messenger options
  if (currentInboxType === 'unofficial') {
    return groupedInboxOptions.value
      .filter(item => {
        // Keep headers
        if (item.type === 'header') {
          // Only keep headers for unofficial and other channels
          return (
            item.label === 'WHATSAPP NÃO OFICIAL' ||
            item.label === 'OUTROS CANAIS'
          );
        }
        // Filter out official and messenger inboxes
        return (
          item.integration_type !== 'official' &&
          item.integration_type !== 'messenger' &&
          item.integration_type !== 'facebook_messenger'
        );
      })
      .filter((item, index, arr) => {
        // Remove empty header sections (headers with no items following)
        if (item.type === 'header') {
          // Check if next item is also a header or if this is the last item
          return index < arr.length - 1 && arr[index + 1].type !== 'header';
        }
        return true;
      });
  }

  // For official and messenger, return all options (though these shouldn't trigger dropdown due to non-interactive UI)
  return groupedInboxOptions.value;
});

// Use broadcast helpers composable
const { messageTypeDisplay, estimatedBroadcastTime } = useBroadcastHelpers({
  message,
  attachments,
  messageVariations,
  isUsingTemplate,
  selectedTemplate,
  recipients,
  minDelay,
  maxDelay,
});

// Computed property para processar a mensagem com variáveis
const processedMessage = computed(() => {
  // Check if we have a variation being previewed
  let baseMessage = message.value;

  if (messageComposerRef.value) {
    const previewMsg = messageComposerRef.value.getPreviewMessage?.();
    if (previewMsg) {
      baseMessage = previewMsg;
    }
  }

  if (!baseMessage) return '';

  let processed = baseMessage;

  // Substituir variáveis comuns
  if (recipients.value.length > 0 && recipients.value[0].name) {
    const firstName = recipients.value[0].name.split(' ')[0];
    const lastName =
      recipients.value[0].name.split(' ').slice(1).join(' ') || firstName;

    processed = processed.replace(/\{\{PRIMEIRO NOME\}\}/g, firstName);
    processed = processed.replace(/\{\{first_name\}\}/g, firstName);
    processed = processed.replace(/\{\{nome\}\}/g, firstName);
    processed = processed.replace(/\{\{sobrenome\}\}/g, lastName);
    processed = processed.replace(/\{\{last_name\}\}/g, lastName);
    processed = processed.replace(
      /\{\{nome_completo\}\}/g,
      recipients.value[0].name
    );
    processed = processed.replace(/\{\{name\}\}/g, recipients.value[0].name);
  }

  // Substituir outras variáveis com placeholders
  processed = processed.replace(
    /\{\{telefone\}\}/g,
    recipients.value[0]?.phone || '+55 11 99999-9999'
  );
  processed = processed.replace(
    /\{\{phone\}\}/g,
    recipients.value[0]?.phone || '+55 11 99999-9999'
  );
  processed = processed.replace(
    /\{\{email\}\}/g,
    recipients.value[0]?.email || 'email@exemplo.com'
  );

  return processed;
});

// All other methods from the original component...
// (Copy all methods from BulkMessage.vue but remove history-related ones)

const formatRecipients = () => {
  return recipients.value.map(recipient => {
    let inboxId = null;

    if (
      recipient.selectedInbox !== undefined &&
      recipient.selectedInbox !== null
    ) {
      inboxId = recipient.selectedInbox;
    } else if (selectedInboxes.value.length > 0) {
      const randomIndex = Math.floor(
        Math.random() * selectedInboxes.value.length
      );
      inboxId = selectedInboxes.value[randomIndex];
    } else if (inboxes.value.length > 0) {
      inboxId = inboxes.value[0].id;
    }

    if (inboxId !== null) {
      inboxId = typeof inboxId === 'string' ? parseInt(inboxId, 10) : inboxId;
      if (isNaN(inboxId)) {
        inboxId = null;
      }
    }

    const formattedPhone = formatPhoneWithDDI(recipient.phone);

    return {
      contact_id: recipient.id || recipient.contact_id || null,
      name: recipient.name || '',
      phone: formattedPhone,
      inbox_id: inboxId,
      customFields: recipient.customFields || {},
      // Include any other additional fields from CSV
      ...Object.keys(recipient).reduce((acc, key) => {
        // Exclude already included fields and internal fields
        if (
          ![
            'id',
            'contact_id',
            'name',
            'phone',
            'inbox_id',
            'customFields',
            'selectedInbox',
          ].includes(key)
        ) {
          acc[key] = recipient[key];
        }
        return acc;
      }, {}),
    };
  });
};

const formatPhoneWithDDI = phone => {
  // Don't add DDI - return phone as provided by user
  // Only clean basic formatting if needed
  return phone;
};

const validateRecipients = () => {
  const hasInboxSelected =
    selectedInboxes.value.length > 0 ||
    recipients.value.some(r => r.selectedInbox);

  if (!hasInboxSelected && inboxes.value.length > 0) {
    selectedInboxes.value = [inboxes.value[0].id];
  }

  return true;
};

const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

/**
 * Check if error is a Network Error (no response from server)
 */
const isNetworkError = error => {
  // Network Error = no response received (connection failed, timeout, offline)
  if (!error.response && error.message === 'Network Error') return true;
  if (error.code === 'ECONNABORTED') return true;
  if (error.code === 'ERR_NETWORK') return true;
  return false;
};

/**
 * Retry delays in seconds: 60s, 90s, 180s
 */
const NETWORK_ERROR_RETRY_DELAYS = [60, 90, 180];

/**
 * Check connectivity to diagnose network error cause
 * Returns: 'internet_down' | 'server_down' | 'ok'
 */
const checkConnectivity = async () => {
  try {
    // First, check if we can reach the internet at all (using a reliable external endpoint)
    await Promise.race([
      fetch('https://www.google.com/generate_204', {
        mode: 'no-cors',
        cache: 'no-store',
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 5000)
      ),
    ]);

    // Internet works, now check our server
    try {
      await axios.get('/api/v1/profile', {
        headers: getAuthHeaders(),
        withCredentials: true,
        timeout: 10000,
      });
      return 'ok'; // Both internet and server are working
    } catch (serverErr) {
      return 'server_down'; // Internet works but server doesn't respond
    }
  } catch (internetErr) {
    return 'internet_down'; // Can't reach internet at all
  }
};

/**
 * Wait with countdown display for network error recovery
 */
const waitWithCountdown = async seconds => {
  sendingProgress.value.networkRetryCountdown = seconds;

  for (let remaining = seconds; remaining > 0; remaining--) {
    if (sendingProgress.value.isCancelled) {
      sendingProgress.value.networkRetryCountdown = 0;
      return false; // Cancelled
    }
    sendingProgress.value.networkRetryCountdown = remaining;
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  sendingProgress.value.networkRetryCountdown = 0;
  return true; // Completed wait
};

// Helper function to detect mobile
const isMobile = () => {
  return window.innerWidth < 1024; // lg breakpoint
};

const uploadFileToActiveStorage = file => {
  if (!accountId.value) {
    return Promise.reject(new Error('missing_account_id'));
  }

  const token = currentUser.value?.access_token;
  const fallbackHeaders = getAuthHeaders();
  const uploadUrl = `/api/v1/accounts/${accountId.value}/bulk_messages/uploads`;

  const headers = {
    ...(fallbackHeaders || {}),
    ...(token ? { api_access_token: token } : {}),
  };

  const formData = new FormData();
  formData.append('file', file);

  return axios
    .post(uploadUrl, formData, {
      headers,
      withCredentials: true,
    })
    .then(response => response.data);
};

// Helper function to prepare attachments for sending
const prepareAttachmentsForSending = async () => {
  if (!attachments.value.length) return [];

  if (isUploading.value) {
    throw new Error('attachments_uploading');
  }

  const attachmentsNeedingUpload = attachments.value.filter(
    attachment => !attachment.blobSignedId && attachment.file instanceof File
  );

  if (attachmentsNeedingUpload.length > 0) {
    isUploading.value = true;
    try {
      const uploadResults = await Promise.all(
        attachmentsNeedingUpload.map(async attachment => {
          const blob = await uploadFileToActiveStorage(attachment.file);
          return { attachment, blob };
        })
      );

      // Update attachments with returned signed_ids/keys
      uploadResults.forEach(({ attachment, blob }) => {
        attachment.blobSignedId = blob.signed_id;
        attachment.blobKey = blob.key;
      });
    } catch (error) {
      throw error;
    } finally {
      isUploading.value = false;
    }
  }

  const missingSignedIds = attachments.value.some(
    attachment => !attachment.blobSignedId
  );

  if (missingSignedIds) {
    throw new Error('attachments_missing_signed_ids');
  }

  return attachments.value
    .map(attachment => attachment.blobSignedId)
    .filter(Boolean);
};

const ensureTemplateMediaUploaded = async templatePayload => {
  if (!templatePayload?.header_media_file) {
    return templatePayload;
  }

  if (isUploading.value) {
    throw new Error('attachments_uploading');
  }

  isUploading.value = true;
  try {
    const blob = await uploadFileToActiveStorage(templatePayload.header_media_file);
    const origin = window.location.origin || '';
    const mediaUrl = `${origin}/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`;

    templatePayload.template_params = templatePayload.template_params || {};
    templatePayload.template_params.processed_params =
      templatePayload.template_params.processed_params || {};
    templatePayload.template_params.processed_params.header =
      templatePayload.template_params.processed_params.header || {};

    templatePayload.template_params.processed_params.header.media_url = mediaUrl;
    templatePayload.header_media_file = null;

    return templatePayload;
  } finally {
    isUploading.value = false;
  }
};

const handleAttachmentPreparationError = error => {
  const message =
    error?.message === 'attachments_uploading'
      ? 'Aguarde o upload do arquivo terminar antes de continuar.'
      : error?.message === 'missing_account_id'
        ? 'Não foi possível identificar a conta para enviar o arquivo.'
        : 'Erro ao preparar o anexo. Tente novamente.';

  try {
    useAlert(message, 'error');
  } catch (e) {
    console.error(message);
  }
};

// Show confirmation modal when user clicks send button
const showConfirmationModal = () => {
  if (!canSend.value) {
    useAlert('Preencha todos os campos obrigatórios', 'error');
    return;
  }
  if (!validateRecipients()) {
    return;
  }

  // Ensure template params are filled before opening confirmation (official WhatsApp)
  if (
    isUsingTemplate.value &&
    selectedTemplate.value &&
    messageTemplateRef.value &&
    !messageTemplateRef.value.validateParameters()
  ) {
    try {
      useAlert(
        'Por favor, preencha todos os parâmetros obrigatórios do template.',
        'error'
      );
    } catch (e) {
      console.error(
        'Por favor, preencha todos os parâmetros obrigatórios do template.'
      );
    }

    setTimeout(() => {
      const element = document.getElementById('template-parameters');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);

    return;
  }

  // Show broadcast confirmation modal immediately
  showBroadcastConfirmationModal.value = true;
};

const sendBroadcast = async () => {
  // Automatically collect variations from MessageComposer if they exist
  const variationsCollected = collectVariationsFromComposer();
  debug('Variations collection result:', variationsCollected);
  debug('Current messageVariations:', messageVariations.value);

  // Check for risky intervals and show confirmation modal
  if (hasRiskyIntervals.value) {
    showRiskConfirmationModal.value = true;
    return;
  }

  // On mobile, if using template, show validation modal instead of sending immediately
  if (isMobile() && isUsingTemplate.value && selectedTemplate.value) {
    showMobileValidationModal.value = true;
    return;
  }

  // Validate template parameters if using template (desktop behavior)
  if (
    isUsingTemplate.value &&
    selectedTemplate.value &&
    messageTemplateRef.value
  ) {
    if (!messageTemplateRef.value.validateParameters()) {
      try {
        useAlert(
          'Por favor, preencha todos os parâmetros obrigatórios do template.',
          'error'
        );
      } catch (e) {
        console.error(
          'Por favor, preencha todos os parâmetros obrigatórios do template.'
        );
      }

      // Navigate to template parameters section
      setTimeout(() => {
        const element = document.getElementById('template-parameters');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);

      return;
    }
  }

  // Proceed with actual sending
  await performBroadcast();
};

// Handle broadcast confirmation with labels
const handleBroadcastConfirmation = async (labels, labelsToRemove = [], atendimentoAction = 'none') => {
  selectedBroadcastLabels.value = [...labels];
  selectedBroadcastLabelsToRemove.value = [...(labelsToRemove || [])];

  // Handle the atendimento-humano label based on action
  if (atendimentoAction === 'add' || atendimentoAction === 'remove') {
    const atendimentoLabel = allContactLabels.value?.find(
      l => l.title.toLowerCase() === 'atendimento-humano'
    );

    if (atendimentoLabel) {
      if (atendimentoAction === 'add') {
        // Add to labels if not already present
        if (!selectedBroadcastLabels.value.some(l => l.id === atendimentoLabel.id)) {
          selectedBroadcastLabels.value.push(atendimentoLabel);
        }
        // Remove from labelsToRemove if present
        selectedBroadcastLabelsToRemove.value = selectedBroadcastLabelsToRemove.value.filter(
          l => l.id !== atendimentoLabel.id
        );
      } else if (atendimentoAction === 'remove') {
        // Add to labelsToRemove if not already present
        if (!selectedBroadcastLabelsToRemove.value.some(l => l.id === atendimentoLabel.id)) {
          selectedBroadcastLabelsToRemove.value.push(atendimentoLabel);
        }
        // Remove from labels if present
        selectedBroadcastLabels.value = selectedBroadcastLabels.value.filter(
          l => l.id !== atendimentoLabel.id
        );
      }
    }
  }

  showBroadcastConfirmationModal.value = false;
  // Now proceed with the actual send logic
  await sendBroadcast();
};

// Handle default inbox selection from modal on page load
const handleDefaultInboxSelection = inboxId => {
  selectedInboxes.value = [inboxId];
  userManuallySetGlobalInbox.value = true;
  showInboxSelectionModal.value = false;

  // Set the selected inbox for all existing recipients
  recipients.value.forEach(r => {
    r.selectedInbox = inboxId;
  });

  debug('Default inbox selected from modal:', inboxId);
};

// Handle creating new label
const handleCreateLabel = async labelData => {
  try {
    // Create the label via store action
    await store.dispatch('labels/create', {
      title: labelData.title,
      color: labelData.color,
    });

    // Reload labels to get the new one
    await store.dispatch('labels/get');

    useAlert(`Label "${labelData.title}" criada com sucesso!`, 'success');
  } catch (error) {
    console.error('Error creating label:', error);
    useAlert('Erro ao criar label', 'error');
  }
};

// Show AddLabel modal using DOM event to communicate with Dashboard
const showAddLabelModal = () => {
  try {
    const event = new CustomEvent('dashboard-show-add-label-modal', {
      bubbles: true,
      detail: { action: 'show' },
    });
    document.dispatchEvent(event);
  } catch (error) {
    console.error('Failed to show AddLabel modal:', error);
    useAlert('Erro ao abrir modal de criar label', 'error');
  }
};

// Schedule broadcast functions
const showScheduleBroadcastModal = () => {
  // Validate before showing modal
  if (recipients.value.length > 2000) {
    try {
      useAlert(
        'O limite máximo é de 2000 destinatários por disparo. Você tem ' +
          recipients.value.length +
          ' destinatários selecionados.',
        'error'
      );
    } catch (e) {
      console.warn('Alert error:', e);
      alert(
        'O limite máximo é de 2000 destinatários por disparo. Você tem ' +
          recipients.value.length +
          ' destinatários selecionados.'
      );
    }
    return;
  }

  if (
    isUsingTemplate.value &&
    selectedTemplate.value &&
    messageTemplateRef.value &&
    !messageTemplateRef.value.validateParameters()
  ) {
    try {
      useAlert(
        'Por favor, preencha todos os parâmetros obrigatórios do template.',
        'error'
      );
    } catch (e) {
      console.warn('Alert error:', e);
    }

    setTimeout(() => {
      const element = document.getElementById('template-parameters');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);

    return;
  }

  showScheduleModal.value = true;
};

const handleScheduleBroadcast = async (scheduledAt, labels = [], labelsToRemove = [], atendimentoAction = 'none') => {
  try {
    isSubmitting.value = true;
    // Garante que variações selecionadas no compositor sejam capturadas antes de agendar
    collectVariationsFromComposer();
    selectedBroadcastLabels.value = [...(labels || [])];
    selectedBroadcastLabelsToRemove.value = [...(labelsToRemove || [])];

    // Handle the atendimento-humano label based on action
    if (atendimentoAction === 'add' || atendimentoAction === 'remove') {
      const atendimentoLabel = allContactLabels.value?.find(
        l => l.title.toLowerCase() === 'atendimento-humano'
      );

      if (atendimentoLabel) {
        if (atendimentoAction === 'add') {
          // Add to labels if not already present
          if (!selectedBroadcastLabels.value.some(l => l.id === atendimentoLabel.id)) {
            selectedBroadcastLabels.value.push(atendimentoLabel);
          }
          // Remove from labelsToRemove if present
          selectedBroadcastLabelsToRemove.value = selectedBroadcastLabelsToRemove.value.filter(
            l => l.id !== atendimentoLabel.id
          );
        } else if (atendimentoAction === 'remove') {
          // Add to labelsToRemove if not already present
          if (!selectedBroadcastLabelsToRemove.value.some(l => l.id === atendimentoLabel.id)) {
            selectedBroadcastLabelsToRemove.value.push(atendimentoLabel);
          }
          // Remove from labels if present
          selectedBroadcastLabels.value = selectedBroadcastLabels.value.filter(
            l => l.id !== atendimentoLabel.id
          );
        }
      }
    }

    const allRecipients = formatRecipients();

    const fallbackInboxId =
      allRecipients.find(r => r.inbox_id)?.inbox_id ||
      selectedInboxes.value[0] ||
      inboxes.value[0]?.id ||
      null;

    const recipientsPayload = allRecipients.map(recipient => ({
      contact_id: recipient.contact_id,
      name: recipient.name,
      phone: recipient.phone,
      inbox_id: recipient.inbox_id,
      custom_fields: recipient.customFields || {},
    }));

    const labelIds =
      selectedBroadcastLabels.value && selectedBroadcastLabels.value.length > 0
        ? selectedBroadcastLabels.value.reduce((acc, label) => {
            acc[label.id] = label.title || label.name;
            return acc;
          }, {})
        : {};

    const removeLabelIds =
      selectedBroadcastLabelsToRemove.value &&
      selectedBroadcastLabelsToRemove.value.length > 0
        ? selectedBroadcastLabelsToRemove.value.reduce((acc, label) => {
            acc[label.id] = label.title || label.name;
            return acc;
          }, {})
        : {};

    const additionalAttributes = {
      recipients: allRecipients,
      labels: selectedBroadcastLabels.value,
      variations: messageVariations.value,
    };

    // Only send interval delay when not using official WhatsApp API
    if (!isUsingOfficialApi.value) {
      additionalAttributes.interval_delay = {
        min: minDelay.value,
        max: maxDelay.value,
      };
    }

    if (Object.keys(labelIds).length > 0) {
      additionalAttributes.label_ids = labelIds;
    }

    if (Object.keys(removeLabelIds).length > 0) {
      additionalAttributes.remove_label_ids = removeLabelIds;
    }

    if (
      selectedBroadcastLabelsToRemove.value &&
      selectedBroadcastLabelsToRemove.value.length > 0
    ) {
      additionalAttributes.remove_labels = selectedBroadcastLabelsToRemove.value;
    }

    // Prepare broadcast data
    const broadcastData = {
      scheduled_at: scheduledAt.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      content: message.value,
      message_type: 'outgoing',
      // Backend content_type enum doesn't include "template"; use text and pass template_params
      content_type: 'text',
      inbox_id: fallbackInboxId,
      message_variations: messageVariations.value,
      target_payload: {
        target_type: 'custom',
        inbox_id: fallbackInboxId,
        recipients: recipientsPayload,
      },
      additional_attributes: additionalAttributes,
    };

    try {
      const scheduledAttachmentIds = await prepareAttachmentsForSending();
      if (scheduledAttachmentIds.length > 0) {
        broadcastData.attachment_ids = scheduledAttachmentIds;
      }
    } catch (error) {
      handleAttachmentPreparationError(error);
      isSubmitting.value = false;
      return;
    }

    // Add template info if using template
    if (isUsingTemplate.value && selectedTemplate.value) {
      broadcastData.target_payload.template_id = selectedTemplate.value.id;

      let templatePayload = messageTemplateRef.value
        ? messageTemplateRef.value.getParameterValues()
        : {};

      try {
        templatePayload = await ensureTemplateMediaUploaded(templatePayload);
      } catch (error) {
        handleAttachmentPreparationError(error);
        isSubmitting.value = false;
        return;
      }

      // API expects template_params inside additional_attributes
      if (templatePayload?.template_params) {
        broadcastData.additional_attributes =
          broadcastData.additional_attributes || {};
        broadcastData.additional_attributes.template_params =
          templatePayload.template_params;
      }
    }

    // Create scheduled broadcast
    const response =
      await ScheduledBroadcastsAPI.createScheduledBroadcast(broadcastData);

    showScheduleModal.value = false;

    useAlert(
      `Disparo agendado com sucesso para ${new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(scheduledAt)}`,
      'success'
    );

    // Go back to step 1
    currentStep.value = 1;
    // Leva direto para a lista de agendados
    router.push({ name: 'bulkMessage_scheduled', params: { accountId: accountId.value } });
  } catch (error) {
    console.error('Error scheduling broadcast:', error);
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.errors?.[0] ||
      'Erro ao agendar disparo';
    useAlert(errorMessage, 'error');
  } finally {
    isSubmitting.value = false;
  }
};

// Separate function for actual broadcast sending
const performBroadcast = async () => {
  if (recipients.value.length > 2000) {
    try {
      useAlert(
        'O limite máximo é de 2000 destinatários por disparo. Você tem ' +
          recipients.value.length +
          ' destinatários selecionados.',
        'error'
      );
    } catch (e) {
      console.warn('Alert error:', e);
      alert(
        'O limite máximo é de 2000 destinatários por disparo. Você tem ' +
          recipients.value.length +
          ' destinatários selecionados.'
      );
    }
    return;
  }

  isSubmitting.value = true;
  showProgressOverlay.value = true;
  isCancelling.value = false;
  sendingProgress.value = {
    broadcastId: currentBroadcastId.value,
    totalRecipients: 0,
    currentProgress: 0,
    successCount: 0,
    failedCount: 0,
    errors: [],
    isCancelled: false,
  };

  const allRecipients = formatRecipients();
  sendingProgress.value.totalRecipients = allRecipients.length;

  const BATCH_SIZE = 5;
  totalBatches.value = Math.ceil(allRecipients.length / BATCH_SIZE);
  currentBatchIndex.value = 0;
  batchStartTimes.value = [];

  debug(
    `Will process ${totalBatches.value} batches of up to ${BATCH_SIZE} recipients each`
  );

  try {
    // Process all recipients together
    await processGroup(
      allRecipients,
      isUsingWhatsappCloud.value,
      allRecipients,
      recipients.value // Pass original recipients for variable replacement
    );
  } catch (error) {
    console.error('Error processing broadcast:', error);
    if (
      error?.message !== 'attachments_uploading' &&
      error?.message !== 'attachments_missing_signed_ids'
    ) {
      useAlert('Erro ao processar disparo', 'error');
    }
  } finally {
    isSubmitting.value = false;

    if (sendingProgress.value.isCancelled && showProgressOverlay.value) {
      debug('Cancellation completed in finally block, closing modal...');
      setTimeout(() => {
        showProgressOverlay.value = false;
        isCancelling.value = false;
      }, 1000);
    }
  }
};

// Handle mobile modal confirmation
const confirmMobileSend = async () => {
  // Button is only clickable when parameters are valid, so we can proceed directly
  showMobileValidationModal.value = false;
  // Proceed with actual sending
  await performBroadcast();
};

// Handle risk confirmation
const confirmRiskySend = async () => {
  showRiskConfirmationModal.value = false;

  // On mobile, if using template, show validation modal instead of sending immediately
  if (isMobile() && isUsingTemplate.value && selectedTemplate.value) {
    showMobileValidationModal.value = true;
    return;
  }

  // Validate template parameters if using template (desktop behavior)
  if (
    isUsingTemplate.value &&
    selectedTemplate.value &&
    messageTemplateRef.value
  ) {
    if (!messageTemplateRef.value.validateParameters()) {
      try {
        useAlert(
          'Por favor, preencha todos os parâmetros obrigatórios do template.',
          'error'
        );
      } catch (e) {
        console.error(
          'Por favor, preencha todos os parâmetros obrigatórios do template.'
        );
      }

      // Navigate to template parameters section
      setTimeout(() => {
        const element = document.getElementById('template-parameters');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);

      return;
    }
  }

  // Proceed with actual sending
  await performBroadcast();
};

const processGroup = async (
  recipients,
  isOfficial,
  allRecipients,
  originalRecipients
) => {
  try {
    const messagePayload = {
      min_delay: minDelay.value,
      max_delay: maxDelay.value,
      delivery_config: { send_immediately: true },
    };

    // Prepare attachments for sending
    let preparedAttachments = [];
    try {
      preparedAttachments = await prepareAttachmentsForSending();
    } catch (error) {
      handleAttachmentPreparationError(error);
      throw error;
    }

    if (isOfficial) {
      let templatePayload = messageTemplateRef.value
        ? messageTemplateRef.value.getParameterValues()
        : null;

      try {
        templatePayload = await ensureTemplateMediaUploaded(templatePayload);
      } catch (error) {
        handleAttachmentPreparationError(error);
        throw error;
      }

      const processedParams =
        templatePayload?.template_params?.processed_params || {};
      const footerValue = templatePayload?.template_params?.footer || '';
      const buttonsValue = templatePayload?.template_params?.buttons || [];

      if (!selectedTemplate.value) {
        throw new Error(
          'Template não selecionado para API oficial do WhatsApp'
        );
      }

      messagePayload.message = '';
      messagePayload.template = {
        name: selectedTemplate.value.name,
        language: selectedTemplate.value.language,
        namespace: selectedTemplate.value.namespace,
        parameters: selectedTemplate.value.parameters,
        footer: footerValue,
        buttons: buttonsValue,
        processed_params: processedParams,
      };

    } else {
      if (!message.value.trim() && attachments.value.length === 0) {
        throw new Error(
          'Mensagem ou arquivo de mídia é obrigatório para API não oficial'
        );
      }

      // Use original message as base - will be randomized per batch if variations exist
      messagePayload.message = message.value;
      messagePayload.template = undefined;
    }

    if (preparedAttachments.length > 0) {
      messagePayload.attachment_ids = preparedAttachments;
    }

    const BATCH_SIZE = 1;
    const batches = [];
    for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
      batches.push(recipients.slice(i, i + BATCH_SIZE));
    }

    debug(
      `Processing ${recipients.length} recipients in ${batches.length} batches of ${BATCH_SIZE}`
    );

    let totalSuccessCount = 0;
    let totalFailedCount = 0;
    let combinedErrors = [];

    for (let i = 0; i < batches.length; i++) {
      if (sendingProgress.value.isCancelled) {
        debug('Broadcast cancelled, stopping batch processing');
        break;
      }

      const batch = batches[i];
      currentBatchIndex.value = i;
      isProcessingBatch.value = true;

      debug(
        `Processing batch ${i + 1}/${batches.length} with ${batch.length} recipients`
      );

      const batchStartTime = Date.now();
      batchStartTimes.value.push(batchStartTime);

      startBatchProgressAnimation(i, batch.length, batches.length);

      try {
        if (sendingProgress.value.isCancelled) {
          debug('Broadcast cancelled before sending request for batch', i + 1);
          break;
        }

        // Prepare request data with individual message variations per recipient
        const requestData = {
          bulk_message: { ...messagePayload },
          recipients: batch,
          all_recipients: i == 0 ? allRecipients : '',
          // Mark if this is the final batch so the backend knows to complete the broadcast
          is_final_batch: i === batches.length - 1,
        };

        // Add labels if selected
        if (
          selectedBroadcastLabels.value &&
          selectedBroadcastLabels.value.length > 0
        ) {
          // Create object with id as key and label name as value
          requestData.label_ids = selectedBroadcastLabels.value.reduce(
            (acc, label) => {
              acc[label.id] = label.title || label.name;
              return acc;
            },
            {}
          );
        }

        if (
          selectedBroadcastLabelsToRemove.value &&
          selectedBroadcastLabelsToRemove.value.length > 0
        ) {
          requestData.remove_label_ids = selectedBroadcastLabelsToRemove.value.reduce(
            (acc, label) => {
              acc[label.id] = label.title || label.name;
              return acc;
            },
            {}
          );
          requestData.remove_labels = selectedBroadcastLabelsToRemove.value;
        }

        // If variations exist, assign random variations per recipient
        if (!isOfficial && messageVariations.value.length > 0) {
          // Create individual message assignments for each recipient
          requestData.recipient_messages = batch.map(
            (recipient, recipientIndex) => {
              // Find the original recipient with custom fields from the current batch
              const originalRecipientIndex = i * BATCH_SIZE + recipientIndex;
              const originalRecipient =
                originalRecipients[originalRecipientIndex];

              const randomMessage = getRandomMessage();
              const personalizedMessage = replaceVariablesWithRecipientData(
                randomMessage,
                originalRecipient
              );
              return {
                recipient_id: recipient.id,
                message: personalizedMessage,
              };
            }
          );

          debug(
            `🎲 Assigned ${batch.length} personalized variations for batch ${i + 1}`
          );
        } else if (!isOfficial) {
          // For regular messages (no variations), still personalize per recipient
          requestData.recipient_messages = batch.map(
            (recipient, recipientIndex) => {
              // Find the original recipient with custom fields from the current batch
              const originalRecipientIndex = i * BATCH_SIZE + recipientIndex;
              const originalRecipient =
                originalRecipients[originalRecipientIndex];

              const personalizedMessage = replaceVariablesWithRecipientData(
                messagePayload.message,
                originalRecipient
              );
              return {
                recipient_id: recipient.id,
                message: personalizedMessage,
              };
            }
          );

          debug(
            `📝 Assigned ${batch.length} personalized messages for batch ${i + 1}`
          );
        }

        if (currentBroadcastId.value) {
          requestData.broadcast_id = currentBroadcastId.value;
        }

        // Network error retry loop
        let response = null;
        let lastError = null;

        for (
          let retryAttempt = 0;
          retryAttempt <= NETWORK_ERROR_RETRY_DELAYS.length;
          retryAttempt++
        ) {
          try {
            response = await axios.post('/api/v1/bulk_messages', requestData, {
              headers: getAuthHeaders(),
              withCredentials: true,
              timeout: 300000,
            });
            // Success - reset retry state and break out of retry loop
            sendingProgress.value.isWaitingNetworkRetry = false;
            sendingProgress.value.networkRetryAttempt = 0;
            sendingProgress.value.connectivityStatus = null;
            break;
          } catch (reqError) {
            lastError = reqError;

            // Only retry for Network Errors
            if (!isNetworkError(reqError)) {
              throw reqError; // Not a network error - don't retry
            }

            // Check if we have more retries available
            if (retryAttempt >= NETWORK_ERROR_RETRY_DELAYS.length) {
              throw reqError; // No more retries - throw error
            }

            // Wait before retry with countdown
            const waitSeconds = NETWORK_ERROR_RETRY_DELAYS[retryAttempt];
            debug(
              `Network Error on batch ${i + 1}, waiting ${waitSeconds}s before retry ${retryAttempt + 1}/3`
            );

            // Check connectivity to diagnose the issue
            const connectivity = await checkConnectivity();
            sendingProgress.value.connectivityStatus = connectivity;

            sendingProgress.value.isWaitingNetworkRetry = true;
            sendingProgress.value.networkRetryAttempt = retryAttempt + 1;

            // Build appropriate message based on connectivity check
            let statusMessage;
            if (connectivity === 'internet_down') {
              statusMessage = `Sem conexao com a internet - tentando novamente em ${waitSeconds}s (tentativa ${retryAttempt + 1}/3)`;
            } else if (connectivity === 'server_down') {
              statusMessage = `Servidor indisponivel - tentando novamente em ${waitSeconds}s (tentativa ${retryAttempt + 1}/3)`;
            } else {
              statusMessage = `Network Error - tentando novamente em ${waitSeconds}s (tentativa ${retryAttempt + 1}/3)`;
            }

            // Add info to errors list (will be removed on success)
            const retryInfo = {
              phone: `Lote ${i + 1} - Aguardando`,
              error: statusMessage,
              isRetryNotice: true,
            };
            combinedErrors.push(retryInfo);
            sendingProgress.value.errors = [...combinedErrors];

            const shouldContinue = await waitWithCountdown(waitSeconds);

            // Remove retry notice from errors
            const noticeIndex = combinedErrors.findIndex(
              e => e.isRetryNotice && e.phone === `Lote ${i + 1} - Aguardando`
            );
            if (noticeIndex !== -1) combinedErrors.splice(noticeIndex, 1);

            if (!shouldContinue) {
              // Cancelled during wait
              sendingProgress.value.isWaitingNetworkRetry = false;
              throw new Error('Cancelled');
            }
          }
        }

        if (!response) {
          throw lastError || new Error('Unknown error');
        }

        if (sendingProgress.value.isCancelled) {
          debug('Broadcast cancelled after API response for batch', i + 1);
          break;
        }

        if (
          response.data &&
          response.data.broadcast_id &&
          !currentBroadcastId.value
        ) {
          currentBroadcastId.value = response.data.broadcast_id;
          sendingProgress.value.broadcastId = response.data.broadcast_id;
        }

        if (response.data.stats) {
          totalSuccessCount += response.data.stats.sent_count || 0;
          totalFailedCount += response.data.stats.failed_count || 0;

          const batchEnd = Math.min((i + 1) * BATCH_SIZE, allRecipients.length);
          sendingProgress.value.successCount = totalSuccessCount;
          sendingProgress.value.failedCount = totalFailedCount;
          sendingProgress.value.currentProgress = batchEnd;

          debug(
            `Batch ${i + 1} completed. Progress: ${batchEnd}/${allRecipients.length}, Success: ${totalSuccessCount}, Failed: ${totalFailedCount}`
          );
        }

        if (sendingProgress.value.isCancelled) {
          debug('Broadcast cancelled after updating progress for batch', i + 1);
          break;
        }

        if (Array.isArray(response.data.errors)) {
          combinedErrors = [...combinedErrors, ...response.data.errors];
          sendingProgress.value.errors = combinedErrors;
        }

        const batchEndTime = Date.now();
        const batchDuration = batchEndTime - batchStartTime;
        updateAverageBatchTime(batchDuration);

        isProcessingBatch.value = false;

        debug(
          `Batch ${i + 1} completed in ${batchDuration}ms. New average: ${averageBatchTime.value}ms`
        );

        if (sendingProgress.value.isCancelled) {
          debug('Broadcast cancelled before delay for next batch');
          break;
        }

        if (i < batches.length - 1) {
          // Use the user's advanced settings for batch delays (convert to milliseconds)
          const minDelayMs = minDelay.value * 1000;
          const maxDelayMs = maxDelay.value * 1000;
          const randomDelay =
            Math.floor(Math.random() * (maxDelayMs - minDelayMs + 1)) +
            minDelayMs;
          debug(
            `🕒 Custom delay before next batch: ${randomDelay}ms (range: ${minDelayMs}-${maxDelayMs}ms)`
          );
          await new Promise(resolve => setTimeout(resolve, randomDelay));

          if (sendingProgress.value.isCancelled) {
            debug(
              'Broadcast cancelled after delay, stopping before next batch'
            );
            break;
          }
        }
      } catch (batchErr) {
        // Reset retry state
        sendingProgress.value.isWaitingNetworkRetry = false;
        sendingProgress.value.networkRetryAttempt = 0;
        sendingProgress.value.connectivityStatus = null;

        console.error(`Error processing batch ${i + 1}:`, batchErr);

        // Enhanced error message for network errors
        let errorMessage;
        if (isNetworkError(batchErr)) {
          errorMessage =
            'Network Error - falhou apos 3 tentativas (60s + 90s + 180s)';
        } else {
          errorMessage =
            batchErr.response?.data?.error ||
            batchErr.message ||
            'Erro desconhecido';
        }

        combinedErrors.push({
          phone: `Erro no lote ${i + 1}`,
          error: errorMessage,
        });
        sendingProgress.value.errors = combinedErrors;
        totalFailedCount += batch.length;
        sendingProgress.value.failedCount = totalFailedCount;
        const batchEnd = Math.min((i + 1) * BATCH_SIZE, allRecipients.length);
        sendingProgress.value.currentProgress = batchEnd;

        const batchEndTime = Date.now();
        const batchDuration = batchEndTime - batchStartTime;
        updateAverageBatchTime(batchDuration);

        isProcessingBatch.value = false;

        if (sendingProgress.value.isCancelled) {
          debug('Broadcast cancelled after error in batch', i + 1);
          break;
        }

        continue;
      }
    }

    isProcessingBatch.value = false;
    currentBatchIndex.value = batches.length;
    currentBroadcastId.value = null;

    if (sendingProgress.value.isCancelled) {
      debug('Cancellation completed, closing modal...');
      setTimeout(() => {
        showProgressOverlay.value = false;
        isCancelling.value = false;
      }, 1000);
    }

    return {
      success: true,
      stats: {
        sent_count: totalSuccessCount,
        failed_count: totalFailedCount,
      },
      errors: combinedErrors,
    };
  } catch (err) {
    console.error('Error in processGroup:', err);
    const errorMessage =
      err.response?.data?.error || err.message || 'Erro desconhecido';
    sendingProgress.value.errors.push({
      phone: 'Erro de grupo',
      error: errorMessage,
    });
    sendingProgress.value.failedCount += recipients.length;
    sendingProgress.value.currentProgress =
      sendingProgress.value.totalRecipients;
    throw err;
  }
};

const updateAverageBatchTime = newBatchTime => {
  const completedBatches = batchStartTimes.value.length;
  if (completedBatches === 1) {
    averageBatchTime.value = newBatchTime;
  } else {
    averageBatchTime.value = Math.round(
      averageBatchTime.value * 0.7 + newBatchTime * 0.3
    );
  }
};

const startBatchProgressAnimation = (
  batchIndex,
  batchSize,
  totalBatchCount
) => {
  debug(
    `Starting progress animation for batch ${batchIndex + 1}/${totalBatchCount}`
  );

  const BATCH_SIZE = 1; // Use actual batch size, not hardcoded 5
  const startProgress = batchIndex * BATCH_SIZE;
  const endProgress = Math.min(
    startProgress + batchSize,
    sendingProgress.value.totalRecipients
  );

  // Use user's custom delay settings for animation timing
  const avgCustomDelay = ((minDelay.value + maxDelay.value) * 1000) / 2; // Average delay in milliseconds
  debug(
    `Custom delay range: ${minDelay.value}s - ${maxDelay.value}s (avg: ${avgCustomDelay}ms)`
  );

  // Estimate batch time based on custom delays + API processing time
  const apiProcessingTime = Math.max(averageBatchTime.value, 500); // Minimum 500ms for API
  const estimatedBatchTime =
    apiProcessingTime + (batchSize > 1 ? avgCustomDelay * 0.8 : 0);
  const animationTime = Math.round(estimatedBatchTime * 0.7); // Animate for 70% of estimated time
  const updateInterval =
    batchSize > 1 ? Math.max(animationTime / batchSize, 1000) : animationTime;

  debug(
    `Batch ${batchIndex + 1}: Animating from ${startProgress} to ${endProgress - 1} over ${estimatedBatchTime}ms`
  );
  debug(
    `Animation details: ${batchSize} recipients, update every ${updateInterval}ms, total animation time: ${animationTime}ms`
  );

  let currentAnimatedProgress = startProgress;

  const animationInterval = setInterval(() => {
    if (
      !isSubmitting.value ||
      !isProcessingBatch.value ||
      currentBatchIndex.value !== batchIndex
    ) {
      clearInterval(animationInterval);
      return;
    }

    if (currentAnimatedProgress < endProgress - 1) {
      currentAnimatedProgress++;

      sendingProgress.value.currentProgress = currentAnimatedProgress;
      sendingProgress.value.successCount = currentAnimatedProgress;

      debug(
        `Animated progress: ${currentAnimatedProgress}/${sendingProgress.value.totalRecipients} (batch ${batchIndex + 1})`
      );
    } else {
      clearInterval(animationInterval);
      debug(
        `Animation complete for batch ${batchIndex + 1}, waiting for API response...`
      );
    }
  }, updateInterval);

  setTimeout(() => {
    clearInterval(animationInterval);
  }, estimatedBatchTime + 5000);
};

const cancelBroadcast = async () => {
  sendingProgress.value.isCancelled = true;
  debug('Cancellation flag set to true');

  const broadcastId =
    currentBroadcastId.value || sendingProgress.value.broadcastId;

  if (!broadcastId) {
    debug('No broadcast ID available, but cancellation flag set');
    return;
  }

  try {
    const headers = getAuthHeaders();

    await axios.post(
      `/api/v1/bulk_messages/${broadcastId}/cancel`,
      {},
      {
        headers,
        withCredentials: true,
      }
    );

    debug('API cancellation request sent successfully');
  } catch (error) {
    console.error('Erro ao cancelar broadcast:', error);
    try {
      useAlert('Erro ao cancelar', 'error');
    } catch (alertErr) {
      console.error('Error showing alert:', alertErr);
    }
  }
};

const handleCancelBroadcast = async () => {
  if (isCancelling.value) return;

  isCancelling.value = true;
  debug('Starting cancellation process...');

  await cancelBroadcast();
};

// Validation function to check for empty values in selected columns
const validateEmptyValues = () => {
  if (!csvMapping.phone || recipients.value.length === 0) {
    return null; // No validation needed if no CSV import or no recipients
  }

  const { phone, name, additional_columns } = csvMapping;
  const allSelectedColumns = [
    phone,
    name,
    ...(additional_columns || []),
  ].filter(Boolean);

  if (allSelectedColumns.length === 0) {
    return null; // No columns selected for validation
  }

  const emptyFields = [];
  let recipientsWithEmpty = 0;
  const problemRecipients = [];

  recipients.value.forEach((recipient, index) => {
    let hasEmptyField = false;

    allSelectedColumns.forEach(column => {
      const value = recipient[column] || recipient.customFields?.[column];
      if (!value || value.toString().trim() === '') {
        if (!emptyFields.includes(column)) {
          emptyFields.push(column);
        }
        hasEmptyField = true;

        // Store which recipient and which field is empty
        problemRecipients.push({
          index,
          recipient,
          emptyField: column,
        });
      }
    });

    if (hasEmptyField) {
      recipientsWithEmpty++;
    }
  });

  if (emptyFields.length > 0) {
    return {
      emptyFields,
      recipientsWithEmpty,
      totalRecipients: recipients.value.length,
      problemRecipients,
    };
  }

  return null;
};

const nextStep = () => {
  if (currentStep.value === 1) {
    // Validate empty values when going from step 1 to step 2
    const validationResult = validateEmptyValues();

    if (validationResult) {
      // Show modal with validation results
      emptyFieldsValidationData.value = validationResult;
      showEmptyValuesModal.value = true;
      return; // Don't proceed to next step yet
    }
  }

  // Proceed to next step if validation passes or we're not on step 1
  if (currentStep.value < 2) {
    currentStep.value++;
  }
};

const handleEmptyValuesModalClose = () => {
  showEmptyValuesModal.value = false;
  emptyFieldsValidationData.value = {};
};

const handleEmptyValuesGoBack = () => {
  showEmptyValuesModal.value = false;
  emptyFieldsValidationData.value = {};
  // Stay on current step - user can review and fix data
};

const handleEmptyValuesContinue = () => {
  showEmptyValuesModal.value = false;
  emptyFieldsValidationData.value = {};
  // Proceed to next step despite empty values
  if (currentStep.value < 2) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    // Save variations state before switching steps
    if (currentStep.value === 2 && messageComposerRef.value) {
      try {
        messageComposerVariationsState.value = {
          generatedVariations:
            messageComposerRef.value.generatedVariations || [],
          selectedVariations: messageComposerRef.value.selectedVariations || [],
        };
        debug('Saved variations state before navigating back:', {
          generatedCount:
            messageComposerVariationsState.value.generatedVariations.length,
          selectedCount:
            messageComposerVariationsState.value.selectedVariations.length,
        });
      } catch (error) {
        debug('Could not save variations state:', error);
      }
    }
    currentStep.value--;
  }
};

const selectTemplate = template => {
  selectedTemplate.value = template;
  isUsingTemplate.value = true;
};

const getFirstSelectedInboxId = () => {
  // Helper function to check if inbox is official WhatsApp API
  const isOfficialWhatsApp = inbox => {
    return (
      inbox &&
      inbox.channel_type === 'whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
    );
  };

  // PRIORITY 1: Look for official WhatsApp API inbox in selected inboxes
  if (selectedInboxes.value.length > 0) {
    const officialWhatsappInbox = inboxes.value.find(
      inbox =>
        selectedInboxes.value.includes(inbox.id) && isOfficialWhatsApp(inbox)
    );
    if (officialWhatsappInbox) {
      debug(
        'Using official WhatsApp inbox from selected:',
        officialWhatsappInbox.id
      );
      return officialWhatsappInbox.id;
    }

    // If no official, look for any WhatsApp inbox in selected
    const whatsappInbox = inboxes.value.find(
      inbox =>
        selectedInboxes.value.includes(inbox.id) &&
        inbox.channel_type === 'whatsapp'
    );
    if (whatsappInbox) {
      return whatsappInbox.id;
    }
    return selectedInboxes.value[0];
  }

  // PRIORITY 2: Look for official WhatsApp API inbox in recipients
  const recipientWithOfficialWhatsapp = recipients.value.find(recipient => {
    if (recipient.selectedInbox) {
      const inbox = inboxes.value.find(i => i.id === recipient.selectedInbox);
      return isOfficialWhatsApp(inbox);
    }
    return false;
  });

  if (recipientWithOfficialWhatsapp) {
    debug(
      'Using official WhatsApp inbox from recipient:',
      recipientWithOfficialWhatsapp.selectedInbox
    );
    return recipientWithOfficialWhatsapp.selectedInbox;
  }

  // PRIORITY 3: Look for any WhatsApp inbox in recipients
  const recipientWithWhatsappInbox = recipients.value.find(recipient => {
    if (recipient.selectedInbox) {
      const inbox = inboxes.value.find(i => i.id === recipient.selectedInbox);
      return inbox && inbox.channel_type === 'whatsapp';
    }
    return false;
  });

  if (recipientWithWhatsappInbox) {
    return recipientWithWhatsappInbox.selectedInbox;
  }

  // PRIORITY 4: Look for any official WhatsApp inbox in all available inboxes
  const officialWhatsappInbox = inboxes.value.find(inbox =>
    isOfficialWhatsApp(inbox)
  );
  if (officialWhatsappInbox) {
    debug(
      'Using official WhatsApp inbox from all inboxes:',
      officialWhatsappInbox.id
    );
    return officialWhatsappInbox.id;
  }

  // FALLBACK: Any WhatsApp inbox
  const firstWhatsappInbox = inboxes.value.find(
    inbox => inbox.channel_type === 'whatsapp'
  );
  return firstWhatsappInbox ? firstWhatsappInbox.id : null;
};

// Copy all the other methods from the original component...
// (handleCSVImport, removeRecipient, updateName, updatePhone, etc.)

// Helper function to get display name for inbox
const getInboxDisplayName = inboxId => {
  const displayName =
    store.getters['inboxMetadata/getInboxDisplayName'](inboxId);
  return displayName || null;
};

const extractInboxId = inboxOrId => {
  if (typeof inboxOrId === 'object' && inboxOrId !== null) {
    return inboxOrId.id;
  }
  return inboxOrId;
};

const getInboxById = inboxId =>
  inboxes.value.find(inbox => inbox.id === inboxId);

const connectedEvolutionInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.integration_type === 'evolution' &&
      inbox.isConnected
  )
);

const normalizeNumberForValidation = number =>
  (number || '').toString().trim();

const selectInboxForValidation = () => {
  if (!connectedEvolutionInboxes.value.length) return null;

  const selectedInboxIds = selectedInboxes.value
    .map(extractInboxId)
    .filter(id => id !== null && id !== undefined);

  const selectedInboxesData = selectedInboxIds
    .map(getInboxById)
    .filter(Boolean);

  // If user selected an Evolution inbox for broadcast, use it
  const selectedUnofficial = selectedInboxesData.find(
    inbox =>
      inbox.integration_type === 'evolution' &&
      inbox.isConnected
  );
  if (selectedUnofficial) return selectedUnofficial.id;

  // If recipients have an Evolution inbox set, use it
  const recipientUnofficial = recipients.value.find(recipient => {
    const inbox = getInboxById(recipient.selectedInbox);
    return inbox && inbox.integration_type === 'evolution' && inbox.isConnected;
  });
  if (recipientUnofficial?.selectedInbox) {
    return recipientUnofficial.selectedInbox;
  }

  // If broadcast is official, pick any connected Evolution inbox
  const randomIndex = Math.floor(
    Math.random() * connectedEvolutionInboxes.value.length
  );
  return connectedEvolutionInboxes.value[randomIndex]?.id || null;
};

// Nova função que retorna válidos/inválidos separados
const runWhatsappNumberValidationWithResult = async newRecipients => {
  if (!newRecipients || newRecipients.length === 0) {
    return { valid: newRecipients || [], invalid: [] };
  }

  const validationInboxId = selectInboxForValidation();
  if (!validationInboxId) {
    debug('Skipping validation: no Evolution inbox connected');
    // Mesmo sem Evolution, separar contatos sem telefone
    const valid = [];
    const invalid = [];
    newRecipients.forEach(recipient => {
      const phone = recipient.phone || recipient.phone_number;
      if (!phone || !phone.toString().trim()) {
        invalid.push({ ...recipient, originalPhone: '', noPhoneNumber: true });
      } else {
        valid.push(recipient);
      }
    });
    return { valid, invalid };
  }

  const numbersPayload = [];
  const seenNumbers = new Set();

  newRecipients.forEach(recipient => {
    const normalizedNumber = normalizeNumberForValidation(
      recipient.phone || recipient.phone_number
    );
    if (normalizedNumber && !seenNumbers.has(normalizedNumber)) {
      seenNumbers.add(normalizedNumber);
      numbersPayload.push(normalizedNumber);
    }
  });

  if (!numbersPayload.length) {
    return { valid: newRecipients, invalid: [] };
  }

  try {
    const response = await InboxMetaAPI.validateWhatsAppNumbers(
      validationInboxId,
      numbersPayload
    );

    const data = Array.isArray(response.data) ? response.data : [];

    const invalidNumbersSet = new Set(
      data
        .filter(item => item.exists === false)
        .map(item => normalizeNumberForValidation(item.number))
    );

    const valid = [];
    const invalid = [];

    newRecipients.forEach(recipient => {
      const normalizedPhone = normalizeNumberForValidation(
        recipient.phone || recipient.phone_number
      );

      // Contatos sem telefone vão direto para inválidos
      if (!normalizedPhone) {
        invalid.push({
          ...recipient,
          originalPhone: '',
          noPhoneNumber: true,
        });
      } else if (invalidNumbersSet.has(normalizedPhone)) {
        invalid.push({
          ...recipient,
          originalPhone: recipient.phone || recipient.phone_number,
        });
      } else {
        valid.push(recipient);
      }
    });

    return { valid, invalid };
  } catch (error) {
    console.error('Erro ao validar números:', error);
    // Em caso de erro, ainda separar contatos sem telefone
    const valid = [];
    const invalid = [];
    newRecipients.forEach(recipient => {
      const phone = recipient.phone || recipient.phone_number;
      if (!phone || !phone.toString().trim()) {
        invalid.push({ ...recipient, originalPhone: '', noPhoneNumber: true });
      } else {
        valid.push(recipient);
      }
    });
    return { valid, invalid };
  }
};

const loadInboxes = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Ensure inbox metadata is loaded before trying to use it
    // This handles the race condition when page is refreshed directly on this route
    if (!store.getters['inboxMetadata/isMetadataLoaded']) {
      debug('Inbox metadata not loaded yet, waiting for it to load...');
      await store.dispatch('inboxMetadata/loadAllMetadata');
    }

    // Get inbox metadata from Vuex store (already loaded by App.vue or just loaded above)
    const inboxMetadata = formattedInboxMetadata.value;

    if (
      !inboxMetadata ||
      !inboxMetadata.results ||
      !Array.isArray(inboxMetadata.results)
    ) {
      console.error('Invalid store data structure:', inboxMetadata);
      inboxes.value = [];
      return;
    }

    // Process inbox groups and avoid duplicates using data from store
    const inboxMap = new Map();
    const data = inboxMetadata; // Use same variable name for compatibility

    data.results.forEach(group => {
      if (!group) return;

      // Handle Official WhatsApp API first, regardless of instances array
      if (group.integration_type === 'official') {
        const displayName = getInboxDisplayName(group.inbox_id);
        const inboxData = {
          id: group.inbox_id || 0,
          name:
            displayName ||
            group.inbox_name ||
            `WhatsApp ${group.display_phone_number || ''}`,
          inbox_name:
            displayName ||
            group.inbox_name ||
            `WhatsApp ${group.display_phone_number || ''}`,
          display_name: displayName,
          integrationId: group.integration_id || '',
          inboxId: group.inbox_id || 0,
          instanceId: '',
          channel_type: 'whatsapp',
          provider: 'whatsapp_cloud_api',
          integration_type: group.integration_type,
          display_phone_number: group.display_phone_number,
          isConnected: true, // Official API is considered always connected
        };
        // Use inbox_id as key to avoid duplicates
        inboxMap.set(group.inbox_id, inboxData);
        return;
      }

      // Handle Messenger (Facebook Page) inboxes
      if (group.integration_type === 'messenger') {
        const displayName = getInboxDisplayName(group.inbox_id);
        // Check connectionStatus from instances array (like official type)
        const connectionStatus = group.instances?.[0]?.connectionStatus;
        const isConnected = connectionStatus === 2;

        const inboxData = {
          id: group.inbox_id || 0,
          name: displayName || group.inbox_name || group.profile || 'Messenger',
          inbox_name: displayName || group.inbox_name || group.profile,
          display_name: displayName,
          integrationId: group.integration_id || '', // page_id
          inboxId: group.inbox_id || 0,
          instanceId: '',
          channel_type: 'messenger',
          provider: 'facebook',
          integration_type: 'messenger',
          profile: group.profile, // page name
          isConnected: isConnected,
        };
        // Use inbox_id as key to avoid duplicates
        inboxMap.set(group.inbox_id, inboxData);
        return;
      }

      // For Evolution/unofficial inboxes with instances
      if (!Array.isArray(group.instances)) {
        return;
      }

      // Check if any instance is connected
      const hasConnectedInstance = group.instances.some(
        inst => inst && inst.connectionStatus === 2
      );

      // Get the first instance (connected or not) for naming
      const firstInstance = group.instances[0];
      if (!firstInstance) return;

      const displayName = getInboxDisplayName(group.inbox_id);
      const nameInbox =
        displayName ||
        firstInstance.Chatwoot?.nameInbox ||
        group.inbox_name ||
        `Inbox ${group.inbox_id}`;

      const inboxData = {
        id: group.inbox_id,
        name: nameInbox,
        inbox_name: displayName || group.inbox_name || nameInbox,
        display_name: displayName,
        integrationId: group.integration_id || '',
        inboxId: group.inbox_id,
        instanceId: firstInstance.id || '',
        channel_type: group.channel_type || 'unknown',
        provider: group.provider || 'unknown',
        integration_type: group.integration_type,
        isConnected: hasConnectedInstance, // Store actual connection status
      };

      // Add inbox regardless of connection status
      inboxMap.set(group.inbox_id, inboxData);
    });

    // Convert map values to array
    inboxes.value = Array.from(inboxMap.values());

    debug('Successfully loaded inboxes:', inboxes.value);

    // Show inbox selection modal if no inbox is selected yet and there are connected inboxes
    if (selectedInboxes.value.length === 0) {
      const connected = inboxes.value.filter(i => i.isConnected);
      if (connected.length > 0) {
        showInboxSelectionModal.value = true;
      }
    }
  } catch (err) {
    console.error('Failed to load inboxes:', err);
    inboxes.value = [];
  } finally {
    loading.value = false;
  }
};

// All other methods and computed properties...
// Copy them from the original BulkMessage.vue

const allContacts = computed(() => {
  return store.getters['contacts/getContacts'];
});

const isFetching = computed(() => {
  return store.getters['contacts/getUIFlags']?.isFetching || false;
});

const contactsMetaData = computed(() => {
  const meta = store.getters['contacts/getMeta'] || {};
  // Use the totalPages from lastContactsResponse if available, otherwise calculate
  const pageSize =
    lastContactsResponse.value?.meta?.pageSize || meta.pageSize || 25;
  const totalPages =
    lastContactsResponse.value?.meta?.totalPages ||
    Math.ceil((meta.count || 0) / pageSize) ||
    1;

  const result = {
    count: meta.count || 0,
    currentPage:
      lastContactsResponse.value?.meta?.currentPage || meta.currentPage || 1,
    totalPages: totalPages,
    pageSize: pageSize,
    hasPreviousPage: (meta.currentPage || 1) > 1,
    hasNextPage: (meta.currentPage || 1) < totalPages,
  };

  debug('ContactsMetaData computed:', {
    count: result.count,
    currentPage: result.currentPage,
    totalPages: result.totalPages,
    pageSize: result.pageSize,
    fromLastResponse: !!lastContactsResponse.value,
  });

  return result;
});

// Handle incoming CSV data
const handleCSVImport = async (csvData, selectedColumns = []) => {
  debug('Handling CSV import:', csvData);

  // Store the additional columns for table display AND sync to csvMapping
  if (selectedColumns && selectedColumns.length > 0) {
    additionalColumns.value = [...selectedColumns];
    // IMPORTANT: Also update csvMapping so it's available for templates
    csvMapping.additional_columns = [...selectedColumns];
  }

  if (!csvData || !Array.isArray(csvData) || csvData.length === 0) {
    console.warn('No CSV data provided or data is empty');
    return;
  }

  const newRecipients = csvData
    .map((row, index) => {
      if (!row || typeof row !== 'object') {
        console.warn(`Invalid row at index ${index}:`, row);
        return null;
      }

      // Allow contacts with phone, email, or identifier (for Facebook Messenger, etc.)
      if (!row.phone && !row.email && !row.identifier) {
        console.warn(`Missing phone/email/identifier in row ${index}:`, row);
        return null;
      }

      const existingContact = findContactByPhone(row.phone);
      const uniqueId = Date.now() + index + Math.floor(Math.random() * 1000);

      const newRecipient = {
        id: uniqueId,
        name: row.name || (existingContact ? existingContact.name : ''),
        phone: row.phone || '',
        email: row.email || (existingContact ? existingContact.email : ''),
        identifier: row.identifier || '', // Include identifier for Facebook Messenger, etc.
        inboxes: existingContact?.inboxes || [],
        selectedInbox:
          row.selectedInbox || existingContact?.selectedInbox || null,
        // Preserve custom fields for additional columns from CSV
        customFields: row.customFields || {},
      };

      if (index === 0) {
      }

      return newRecipient;
    })
    .filter(Boolean);

  debug(`Adding ${newRecipients.length} new recipients from CSV`);

  let addedCount = 0;
  let skippedDuplicates = 0;
  let skippedOverLimit = 0;

  // Filter out duplicates and respect the limit
  const recipientsToAdd = [];
  newRecipients.forEach(newRecipient => {
    if (!recipients.value.some(r => r.phone === newRecipient.phone)) {
      if (recipients.value.length + recipientsToAdd.length >= 2000) {
        skippedOverLimit++;
      } else {
        recipientsToAdd.push(newRecipient);
        addedCount++;
      }
    } else {
      skippedDuplicates++;
      debug(`Skipping duplicate: ${newRecipient.phone}`);
    }
  });

  // Validate numbers first, then separate valid/invalid
  if (recipientsToAdd.length > 0) {
    const validationResult =
      await runWhatsappNumberValidationWithResult(recipientsToAdd);

    // Add only valid recipients to main list
    if (validationResult.valid.length > 0) {
      recipients.value = [...recipients.value, ...validationResult.valid];
    }

    // Add invalid recipients to separate list
    if (validationResult.invalid.length > 0) {
      invalidRecipients.value = [
        ...invalidRecipients.value,
        ...validationResult.invalid,
      ];
    }

    debug(
      `Added ${validationResult.valid.length} valid recipients. ${validationResult.invalid.length} invalid. Skipped ${skippedDuplicates} duplicates and ${skippedOverLimit} over limit. Total count: ${recipients.value.length}`
    );
  } else {
    debug(
      `No recipients to add. Skipped ${skippedDuplicates} duplicates and ${skippedOverLimit} over limit.`
    );
  }

  if (skippedOverLimit > 0) {
    const message = `Limite de 2000 destinatários atingido. ${skippedOverLimit} contatos não foram adicionados.`;
    try {
      useAlert(message, 'warning');
    } catch (e) {
      console.warn('Alert error:', e);
      alert(message);
    }
  }

  // Only auto-select first inbox if user hasn't manually selected one
  if (
    !userManuallySetGlobalInbox.value &&
    recipients.value.length > 0 &&
    selectedInboxes.value.length === 0 &&
    inboxes.value.length > 0
  ) {
    const firstInboxId = inboxes.value[0].id;

    debug(`Auto-selecting first inbox (${firstInboxId}) for imported contacts`);
    selectedInboxes.value = [firstInboxId];

    recipients.value.forEach(recipient => {
      if (!recipient.selectedInbox) {
        recipient.selectedInbox = firstInboxId;
      }
    });
  }
};

const findContactByPhone = phone => {
  return contacts.value.find(
    contact => contact.phone === phone || contact.phone_number === phone
  );
};

const addRecipientFromLabel = labelId => {
  store.dispatch('contacts/getContactsByLabel', labelId).then(labelContacts => {
    const newRecipients = labelContacts.map(contact => ({
      id: contact.id,
      name: contact.name,
      phone: contact.phone_number,
      email: contact.email,
      inboxes: contact.inboxes || [],
    }));

    let skippedCount = 0;
    newRecipients.forEach(newRecipient => {
      if (!recipients.value.some(r => r.phone === newRecipient.phone)) {
        if (recipients.value.length >= 2000) {
          skippedCount++;
        } else {
          recipients.value.push(newRecipient);
        }
      }
    });

    if (skippedCount > 0) {
      try {
        useAlert(
          `Limite de 2000 destinatários atingido. ${skippedCount} contatos não foram adicionados.`,
          'warning'
        );
      } catch (e) {
        console.warn('Alert error:', e);
        alert(
          `Limite de 2000 destinatários atingido. ${skippedCount} contatos não foram adicionados.`
        );
      }
    }
  });
};

const loadContacts = async () => {
  try {
    debug('Carregando contatos iniciais...');
    const pageSize = 25;
    store.commit('contacts/CLEAR_CONTACTS');

    // Clear the last response when loading initial contacts
    lastContactsResponse.value = null;

    const result = await loadContactsPage({
      page: 1,
      pageSize: pageSize,
      query: undefined,
    });

    if (result && result.success) {
      debug(
        `Carregados com sucesso ${result.contacts.length} contatos com metadados:`,
        result.meta
      );
    } else {
      console.error('Falha ao carregar contatos iniciais');
    }
  } catch (error) {
    console.error('Erro em loadContacts:', error);
    useAlert('Erro ao carregar contatos iniciais', 'error');
  }
};

const loadContactsPage = async ({ page, pageSize, query, labels, labelsFilterMode, channel_type }) => {
  try {
    const requestPage = Math.max(1, page || 1);
    const requestSize = Math.max(10, pageSize || 25);
    const queryParam = query || '';
    const channelTypeParam = channel_type ? String(channel_type).trim() : '';

    debug(
      `Loading page ${requestPage} of contacts with size ${requestSize}${queryParam ? ` and search '${queryParam}'` : ''}${labels && labels.length > 0 ? ` and labels [${labels.join(', ')}] (mode: ${labelsFilterMode || 'or'})` : ''}${channelTypeParam ? ` and channel '${channelTypeParam}'` : ''}`
    );

    store.commit('contacts/SET_CONTACT_UI_FLAG', { isFetching: true });

    const requestUrl = '/api/v1/bulk_messages/contacts';

    const response = await axios.get(requestUrl, {
      params: {
        page: requestPage,
        per_page: requestSize,
        query: queryParam || undefined,
        labels: labels && labels.length > 0 ? labels : undefined,
        labels_filter_mode: labelsFilterMode || 'and', // 'and' = contacts must have ALL labels, 'or' = contacts can have ANY label
        exclude_empty_phones: true, // Include contacts with phone, email, or identifier
        channel_type: channelTypeParam || undefined,
      },
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    if (!response.data) {
      console.error('API response does not contain the data property');
      store.commit('contacts/SET_CONTACT_UI_FLAG', { isFetching: false });
      return { success: false };
    }

    const contactsData = response.data.data || [];
    const meta = response.data.meta || {};

    debug('API Response Meta:', {
      total_count: meta.total_count,
      current_page: meta.current_page,
      total_pages: meta.total_pages,
      per_page: meta.per_page,
    });

    store.commit('contacts/SET_CONTACTS', contactsData);

    const normalizedCount = Number(meta.total_count) || 0;
    const normalizedCurrentPage =
      Number(meta.current_page || requestPage) || 1;
    const normalizedTotalPages =
      Number(meta.total_pages) ||
      Math.ceil(normalizedCount / requestSize) ||
      1;

    const metaData = {
      count: normalizedCount,
      current_page: normalizedCurrentPage,
      pageSize: requestSize,
    };

    debug('Setting contact metadata in store:', metaData);
    store.commit('contacts/SET_CONTACT_META', metaData);

    store.commit('contacts/SET_CONTACT_UI_FLAG', { isFetching: false });

    pagination.value.pageSize = requestSize;

    // Store the complete response metadata
    const responseMetadata = {
      count: normalizedCount,
      currentPage: normalizedCurrentPage,
      totalPages: normalizedTotalPages,
      pageSize: requestSize,
    };

    // Store the response for use in computed properties
    lastContactsResponse.value = {
      contacts: contactsData,
      meta: responseMetadata,
    };

    return {
      success: true,
      contacts: contactsData,
      meta: responseMetadata,
    };
  } catch (error) {
    console.error(`Error loading contacts page ${page}:`, error);

    store.commit('contacts/SET_CONTACT_UI_FLAG', { isFetching: false });

    try {
      useAlert(
        error.response?.data?.message ||
          error.message ||
          'Error loading contacts',
        'error'
      );
    } catch (alertErr) {
      console.error('Error showing alert:', alertErr);
    }

    return {
      success: false,
      error,
    };
  }
};

const removeRecipient = id => {
  recipients.value = recipients.value.filter(r => r.id !== id);
};

const updateName = (id, name) => {
  const index = recipients.value.findIndex(r => r.id === id);
  if (index !== -1) {
    recipients.value[index].name = name;
  }
};

const updateCustomField = (id, fieldName, value) => {
  const index = recipients.value.findIndex(r => r.id === id);
  if (index !== -1) {
    // Initialize customFields if it doesn't exist
    if (!recipients.value[index].customFields) {
      recipients.value[index].customFields = {};
    }
    recipients.value[index].customFields[fieldName] = value;
  }
};

const updatePhone = (id, phone) => {
  const index = recipients.value.findIndex(r => r.id === id);
  if (index !== -1) {
    recipients.value[index].phone = phone;
  }
};

const updateEmail = (id, email) => {
  const index = recipients.value.findIndex(r => r.id === id);
  if (index !== -1) {
    recipients.value[index].email = email;
  }
};

const updateInbox = (id, inboxId) => {
  const index = recipients.value.findIndex(r => r.id === id);
  const oldInbox = index !== -1 ? recipients.value[index].selectedInbox : null;

  if (index !== -1) {
    // Use Vue's reactivity system to ensure the change is detected
    // Create a new object with the updated selectedInbox to trigger reactivity
    recipients.value[index] = {
      ...recipients.value[index],
      selectedInbox: inboxId,
    };
  }
};

const deleteSelectedRecipients = ids => {
  debug('Deleting selected recipients:', ids);
  recipients.value = recipients.value.filter(r => !ids.includes(r.id));
  selectedRecipientIds.value = selectedRecipientIds.value.filter(
    id => !ids.includes(id)
  );
};

// Handler para re-validar números editados do modal de inválidos
const handleInvalidNumbersRevalidation = async ({ edits = [], ignored = [] }) => {
  isValidatingNumbers.value = true;

  try {
    const ignoredSet = new Set(
      ignored.map(n => normalizeNumberForValidation(n))
    );

    // Filtra ignorados e aplica edições
    const recipientsToRevalidate = [];
    const unchangedRecipients = [];

    invalidRecipients.value.forEach(recipient => {
      const normalizedPhone = normalizeNumberForValidation(recipient.phone);

      // Pula os ignorados
      if (ignoredSet.has(normalizedPhone)) return;

      const edit = edits.find(
        e => normalizeNumberForValidation(e.original) === normalizedPhone
      );

      if (edit) {
        // Número foi editado - precisa re-validar
        recipientsToRevalidate.push({
          ...recipient,
          phone: edit.updated,
          phone_number: edit.updated,
        });
      } else {
        // Não editado - mantém na lista de inválidos
        unchangedRecipients.push(recipient);
      }
    });

    // Re-valida números editados
    if (recipientsToRevalidate.length > 0) {
      const result =
        await runWhatsappNumberValidationWithResult(recipientsToRevalidate);

      // Válidos vão para lista principal
      if (result.valid.length > 0) {
        recipients.value = [...recipients.value, ...result.valid];
        // Mostra mensagem de sucesso no modal
        invalidNumbersModalRef.value?.showSuccessMessage(result.valid.length);
      }

      // Ainda inválidos permanecem na lista separada junto com os não alterados
      invalidRecipients.value = [...result.invalid, ...unchangedRecipients];
    } else {
      // Nenhum editado, só atualiza removendo os ignorados
      invalidRecipients.value = unchangedRecipients;
    }

    // Fecha o modal após verificar
    showInvalidNumbersModal.value = false;
  } finally {
    isValidatingNumbers.value = false;
  }
};

// Handler para remover todos os números inválidos
const handleRemoveAllInvalid = () => {
  invalidRecipients.value = [];
  showInvalidNumbersModal.value = false;
};

// Helper function to log current inbox distribution
const logInboxState = caller => {
  const inboxCounts = {};
  recipients.value.forEach(r => {
    const key = r.selectedInbox || 'unset';
    inboxCounts[key] = (inboxCounts[key] || 0) + 1;
  });
};

// NEW SIMPLIFIED FUNCTION: Set all recipients to the same inbox
const setAllRecipientsInbox = inboxId => {
  // Update all recipients with proper reactivity
  recipients.value = recipients.value.map(recipient => ({
    ...recipient,
    selectedInbox: inboxId,
  }));

  // Update global state
  selectedInboxes.value = [inboxId];
  userManuallySetGlobalInbox.value = true;

  // Log the final state
  logInboxState('BulkMessageNew.setAllRecipientsInbox COMPLETED');

  console.log('Successfully updated all recipients');

  // Return success
  return true;
};

const updateMultipleInboxes = (recipientIds, inboxId) => {
  console.log('[BulkMessageNew.updateMultipleInboxes] Bulk update starting:', {
    timestamp: new Date().toISOString(),
    recipientIds: recipientIds.slice(0, 5), // Log first 5 for brevity
    recipientCount: recipientIds.length,
    targetInboxId: inboxId,
    totalRecipients: recipients.value.length,
    isFullUpdate: recipientIds.length === recipients.value.length,
  });

  if (!recipientIds.length) return;

  // Mark as manual selection when updating all recipients
  if (recipientIds.length === recipients.value.length) {
    userManuallySetGlobalInbox.value = true;
    debug('User manually set global inbox - disabling auto-default');
  }

  recipientIds.forEach(id => {
    updateInbox(id, inboxId);
  });

  // When converting all recipients to the same inbox type,
  // we should set the global selected inbox to only this inbox
  // to avoid mixing different API types
  const targetInbox = inboxes.value.find(inbox => inbox.id === inboxId);
  if (targetInbox) {
    const isOfficialApi = targetInbox.provider === 'whatsapp_cloud_api';

    // Filter out inboxes of the opposite type
    const filteredInboxes = selectedInboxes.value.filter(id => {
      const inbox = inboxes.value.find(i => i.id === id);
      if (!inbox) return false;
      const inboxIsOfficial = inbox.provider === 'whatsapp_cloud_api';
      return inboxIsOfficial === isOfficialApi;
    });

    // Add the new inbox if not already present
    if (!filteredInboxes.includes(inboxId)) {
      filteredInboxes.push(inboxId);
    }

    selectedInboxes.value = filteredInboxes;
    debug(
      `updateMultipleInboxes: Updated global selectedInboxes to same API type:`,
      selectedInboxes.value
    );
  } else if (!selectedInboxes.value.includes(inboxId)) {
    selectedInboxes.value = [...selectedInboxes.value, inboxId];
    debug(
      `updateMultipleInboxes: Added inbox ${inboxId} to global selectedInboxes array:`,
      selectedInboxes.value
    );
  }

  // Log final inbox distribution
  logInboxState('BulkMessageNew.updateMultipleInboxes COMPLETED');
};

const closeProgressOverlay = () => {
  showProgressOverlay.value = false;
  isCancelling.value = false;
};

// Handle random inbox distribution update
const handleRandomInboxesUpdate = randomInboxIds => {
  // Update selectedInboxes to include all inboxes used in random distribution
  selectedInboxes.value = [...randomInboxIds];
  debug(
    'Updated selectedInboxes for random distribution:',
    selectedInboxes.value
  );
};

// Handle confirmed message variations from MessageComposer
const handleVariationsConfirmed = variations => {
  debug('Received variations for random distribution:', variations);
  messageVariations.value = variations;
};

// Automatically collect variations from MessageComposer if they exist
const collectVariationsFromComposer = () => {
  if (messageComposerRef.value) {
    try {
      // Access the exposed reactive refs from the component
      const generatedVariations = messageComposerRef.value.generatedVariations;
      const selectedVariations = messageComposerRef.value.selectedVariations;

      debug('Accessing MessageComposer variations:', {
        hasComponent: !!messageComposerRef.value,
        generatedCount: generatedVariations?.length || 0,
        selectedCount: selectedVariations?.length || 0,
        generatedVariations: generatedVariations,
        selectedVariations: selectedVariations,
      });

      // Store the full state for preservation
      messageComposerVariationsState.value = {
        generatedVariations: generatedVariations || [],
        selectedVariations: selectedVariations || [],
      };

      if (
        generatedVariations &&
        selectedVariations &&
        selectedVariations.length > 0
      ) {
        const selectedTexts = generatedVariations
          .filter(v => selectedVariations.includes(v.id))
          .map(v => v.text);

        if (selectedTexts.length > 0) {
          debug('Auto-collected variations:', selectedTexts);
          messageVariations.value = selectedTexts;
          return true;
        }
      }
    } catch (error) {
      debug('Could not access variations from MessageComposer:', error);
    }
  }

  debug('No variations collected from MessageComposer');
  return false;
};

// Get a random message variation or the original message
const getRandomMessage = () => {
  if (messageVariations.value.length === 0) {
    return message.value;
  }

  const randomIndex = Math.floor(
    Math.random() * messageVariations.value.length
  );
  const selectedVariation = messageVariations.value[randomIndex];
  debug(
    `🎲 Selected variation ${randomIndex + 1}/${messageVariations.value.length}: "${selectedVariation.substring(0, 50)}..."`
  );
  return selectedVariation;
};

// Replace all variables in message with actual recipient data
const replaceVariablesWithRecipientData = (messageText, recipient) => {
  if (!messageText || !recipient) return messageText;

  let processed = messageText;

  // Replace basic variables
  if (recipient.name) {
    const firstName = recipient.name.split(' ')[0];
    const lastName = recipient.name.split(' ').slice(1).join(' ') || firstName;

    processed = processed.replace(/\{\{nome\}\}/g, firstName);
    processed = processed.replace(/\{\{first_name\}\}/g, firstName);
    processed = processed.replace(/\{\{nome_completo\}\}/g, recipient.name);
    processed = processed.replace(/\{\{name\}\}/g, recipient.name);
    processed = processed.replace(/\{\{sobrenome\}\}/g, lastName);
    processed = processed.replace(/\{\{last_name\}\}/g, lastName);
  }

  // Replace phone
  if (recipient.phone) {
    processed = processed.replace(/\{\{telefone\}\}/g, recipient.phone);
    processed = processed.replace(/\{\{phone\}\}/g, recipient.phone);
  }

  // Replace email
  if (recipient.email) {
    processed = processed.replace(/\{\{email\}\}/g, recipient.email);
  }

  // Replace custom field variables
  const allCustomFields = new Set();

  // Add from additionalTemplateColumns
  if (
    additionalTemplateColumns.value &&
    additionalTemplateColumns.value.length > 0
  ) {
    additionalTemplateColumns.value.forEach(column =>
      allCustomFields.add(column)
    );
  }

  // Add from recipient customFields (excluding DDI)
  if (recipient.customFields) {
    Object.keys(recipient.customFields).forEach(field => {
      const lowerField = field.toLowerCase();
      if (!lowerField.includes('ddi') && !lowerField.includes('country')) {
        allCustomFields.add(field);
      }
    });
  }

  // Replace all custom field variables
  allCustomFields.forEach(column => {
    const upperColumn = column.toUpperCase();
    const regex = new RegExp(`\\{\\{${upperColumn}\\}\\}`, 'g');
    const value = recipient[column] || recipient.customFields?.[column] || '';
    if (value) {
      processed = processed.replace(regex, value);
    }
  });

  return processed;
};

const processFileUpload = async files => {
  if (!files || !files.length) {
    return;
  }

  const file = files[0];
  // Clean up existing object URLs
  attachments.value.forEach(attachment => {
    if (attachment.url) {
      URL.revokeObjectURL(attachment.url);
    }
  });

  // Store file locally; upload happens only when sending/scheduling
  attachments.value = [
    {
      id: Date.now(),
      name: file.name,
      type: file.type,
      size: file.size,
      file,
      url: URL.createObjectURL(file),
      blobSignedId: null,
      blobKey: null,
    },
  ];
};

// Document-level drag detection to notify MessageComposer
const handleDocumentDragEnter = event => {
  // Check if the drag contains files
  if (
    event.dataTransfer &&
    event.dataTransfer.types &&
    (event.dataTransfer.types.includes('Files') ||
      event.dataTransfer.types.indexOf('Files') !== -1)
  ) {
    documentDragCounter.value++;
    if (documentDragCounter.value === 1) {
      isDraggingFile.value = true;
    }
  }
};

const handleDocumentDragLeave = event => {
  documentDragCounter.value--;
  if (documentDragCounter.value === 0) {
    isDraggingFile.value = false;
  }
};

const handleDocumentDrop = event => {
  // Reset the dragging state
  documentDragCounter.value = 0;
  isDraggingFile.value = false;
  // Let MessageComposer handle the actual drop
};

const handleDocumentDragOver = event => {
  if (
    event.dataTransfer &&
    event.dataTransfer.types &&
    (event.dataTransfer.types.includes('Files') ||
      event.dataTransfer.types.indexOf('Files') !== -1)
  ) {
    event.preventDefault();
  }
};

// Inbox dropdown handlers
const handleInboxDropdownToggle = (recipientId, buttonElement) => {
  if (activeInboxDropdown.value === recipientId) {
    // Close if already open
    activeInboxDropdown.value = null;
    updateScrollLock(); // Remove scroll lock
    return;
  }

  // Calculate position
  const rect = buttonElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const dropdownHeight = 240; // Estimated max height

  // Check if there's enough space below
  const spaceBelow = viewportHeight - rect.bottom;
  const shouldShowAbove =
    spaceBelow < dropdownHeight && rect.top > dropdownHeight;

  // Calculate position using the same approach as EventModal
  if (shouldShowAbove) {
    // When showing above, use bottom positioning like EventModal does
    dropdownPosition.value = {
      left: rect.right - 250, // 250px is dropdown width
      bottom: viewportHeight - rect.top + 8, // Position from bottom, 8px gap above button
      top: undefined, // Clear top when using bottom
    };
  } else {
    // When showing below, use top positioning
    dropdownPosition.value = {
      left: rect.right - 250, // 250px is dropdown width
      top: rect.bottom + 8, // Position from top, 8px gap below button
      bottom: undefined, // Clear bottom when using top
    };
  }

  activeInboxDropdown.value = recipientId;
  updateScrollLock(); // Add scroll lock when opening
};

const closeInboxDropdown = () => {
  activeInboxDropdown.value = null;
  updateScrollLock(); // Remove scroll lock when closing
};

// Helper function to check if inbox is Messenger
const isMessengerInbox = inbox => {
  if (!inbox) return false;
  return (
    inbox.integration_type === 'messenger' || inbox.channel_type === 'messenger'
  );
};

// Helper function to check if inbox is WhatsApp (official or unofficial)
const isWhatsAppInbox = inbox => {
  if (!inbox) return false;
  const isOfficial =
    inbox.integration_type === 'whatsapp_cloud' ||
    inbox.integration_type === 'official';
  const isUnofficial = inbox.integration_type === 'evolution';
  return isOfficial || isUnofficial;
};

const handleInboxSelection = (recipientId, inboxId) => {
  // Get the inbox being selected
  const selectedInbox = inboxes.value.find(inbox => inbox.id === inboxId);

  if (!selectedInbox) {
    updateInbox(recipientId, inboxId);
    closeInboxDropdown();
    return;
  }

  // STEP 1: Check Messenger vs WhatsApp mixing
  const selectedIsMessenger = isMessengerInbox(selectedInbox);
  const selectedIsWhatsApp = isWhatsAppInbox(selectedInbox);

  if (selectedIsMessenger) {
    // Check if any recipients have WhatsApp inboxes
    for (const recipient of recipients.value) {
      // Skip the current recipient being updated
      if (recipient.id === recipientId) continue;

      // Skip if recipient has no inbox selected
      if (!recipient.selectedInbox) continue;

      // Get the recipient's current inbox
      const recipientInbox = inboxes.value.find(
        inbox => inbox.id === recipient.selectedInbox
      );
      if (!recipientInbox) continue;

      // Check if it's WhatsApp
      if (isWhatsAppInbox(recipientInbox)) {
        useAlert(
          'Não é possível misturar inboxes do Messenger com WhatsApp. Remova os contatos com WhatsApp primeiro.',
          'error'
        );
        closeInboxDropdown();
        return;
      }
    }
  }

  if (selectedIsWhatsApp) {
    // Check if any recipients have Messenger inboxes
    for (const recipient of recipients.value) {
      // Skip the current recipient being updated
      if (recipient.id === recipientId) continue;

      // Skip if recipient has no inbox selected
      if (!recipient.selectedInbox) continue;

      // Get the recipient's current inbox
      const recipientInbox = inboxes.value.find(
        inbox => inbox.id === recipient.selectedInbox
      );
      if (!recipientInbox) continue;

      // Check if it's Messenger
      if (isMessengerInbox(recipientInbox)) {
        useAlert(
          'Não é possível misturar inboxes do WhatsApp com Messenger. Remova os contatos com Messenger primeiro.',
          'error'
        );
        closeInboxDropdown();
        return;
      }
    }
  }

  // STEP 2: Check if the selected inbox is official
  const isSelectedOfficial =
    selectedInbox.integration_type === 'whatsapp_cloud' ||
    selectedInbox.integration_type === 'official';

  if (isSelectedOfficial) {
    // Check all other recipients' inboxes for unofficial ones
    for (const recipient of recipients.value) {
      // Skip the current recipient being updated
      if (recipient.id === recipientId) continue;

      // Skip if recipient has no inbox selected
      if (!recipient.selectedInbox) continue;

      // Get the recipient's current inbox
      const recipientInbox = inboxes.value.find(
        inbox => inbox.id === recipient.selectedInbox
      );
      if (!recipientInbox) continue;

      // Check if it's unofficial
      const isRecipientOfficial =
        recipientInbox.integration_type === 'whatsapp_cloud' ||
        recipientInbox.integration_type === 'official';

      if (!isRecipientOfficial) {
        // Found an unofficial inbox - cannot mix
        useAlert(
          'Não é possível misturar inboxes oficiais com não oficiais.',
          'error'
        );
        closeInboxDropdown();
        return;
      }

      // Check if it's a different official inbox (different phone number)
      if (
        isRecipientOfficial &&
        recipientInbox.display_phone_number !==
          selectedInbox.display_phone_number
      ) {
        useAlert('Não é possível misturar inboxes oficiais.', 'error');
        closeInboxDropdown();
        return;
      }
    }
  } else {
    // Selected inbox is unofficial - check if any official inboxes exist
    for (const recipient of recipients.value) {
      // Skip the current recipient being updated
      if (recipient.id === recipientId) continue;

      // Skip if recipient has no inbox selected
      if (!recipient.selectedInbox) continue;

      // Get the recipient's current inbox
      const recipientInbox = inboxes.value.find(
        inbox => inbox.id === recipient.selectedInbox
      );
      if (!recipientInbox) continue;

      // Check if it's official
      const isRecipientOfficial =
        recipientInbox.integration_type === 'whatsapp_cloud' ||
        recipientInbox.integration_type === 'official';

      if (isRecipientOfficial) {
        // Found an official inbox - cannot mix unofficial with official
        useAlert(
          'Não é possível misturar inboxes não oficiais com oficiais.',
          'error'
        );
        closeInboxDropdown();
        return;
      }
      // Different unofficial inboxes are allowed, so no check needed for that
    }
  }

  // Validation passed, update the inbox
  updateInbox(recipientId, inboxId);
  closeInboxDropdown();
};

// Update scroll lock based on dropdown state
const updateScrollLock = () => {
  if (activeInboxDropdown.value) {
    // Add event listeners to prevent scrolling
    document.body.style.overflow = 'hidden'; // Also use CSS overflow hidden
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
  } else {
    // Remove event listeners and restore scrolling
    document.body.style.overflow = ''; // Restore original overflow
    document.removeEventListener('wheel', preventScroll);
    document.removeEventListener('touchmove', preventScroll);
  }
};

// Prevent scroll event
const preventScroll = e => {
  // Only prevent if the scroll is not inside the dropdown itself
  if (!e.target.closest('[data-inbox-dropdown]')) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
};

// Helper function to get inbox type icon
const getInboxTypeIcon = type => {
  switch (type?.toLowerCase()) {
    case 'whatsapp':
    case 'messenger':
      return 'i-lucide-message-circle';
    case 'facebook':
      return 'i-lucide-facebook';
    case 'twitter':
      return 'i-lucide-twitter';
    case 'email':
      return 'i-lucide-mail';
    case 'web':
      return 'i-lucide-globe';
    case 'telegram':
      return 'i-lucide-send';
    default:
      return 'i-lucide-message-square';
  }
};

// Watch for WhatsApp Cloud API selection to auto-enable templates
watch(
  () => isUsingWhatsappCloud.value,
  newValue => {
    if (newValue && currentStep.value === 2) {
      // Automatically enable template selection for WhatsApp Cloud API
      isUsingTemplate.value = true;
    }
  }
);

// Watch for step changes to auto-enable templates when reaching step 2 with WhatsApp Cloud API
watch(
  () => currentStep.value,
  newStep => {
    if (newStep === 2 && isUsingWhatsappCloud.value) {
      // Automatically enable template selection for WhatsApp Cloud API
      isUsingTemplate.value = true;
    }
  }
);

// Watch for API type changes to set appropriate delays
watch(
  isUsingOfficialApi,
  isOfficial => {
    if (isOfficial) {
      // Official API: Use fixed 0.1s delay for optimal rate (up to 600 msg/min)
      minDelay.value = 0.1;
      maxDelay.value = 0.1;
    } else {
      // Unofficial API: Reset to safe defaults if coming from official
      // Only reset if current values are at 0.1 (meaning they were set by official API)
      if (minDelay.value === 0.1 && maxDelay.value === 0.1) {
        minDelay.value = 15;
        maxDelay.value = 60;
      }
    }
  },
  { immediate: true }
); // Run immediately to set correct initial values

// Watch for loading state to control scrolling of main content only
watch(loading, isLoading => {
  // Find the main content container - adjust selector as needed
  const mainContent = document.querySelector('.calendar-container');

  if (mainContent) {
    if (isLoading) {
      // Disable scrolling only for main content
      mainContent.style.overflow = 'hidden';
      mainContent.style.height = '100vh';
      mainContent.style.position = 'relative';
    } else {
      // Re-enable scrolling for main content
      mainContent.style.overflow = '';
      mainContent.style.height = '';
      mainContent.style.position = '';
    }
  }
});

// Watch for when inboxes are loaded to select pending inbox IDs
watch(inboxes, newInboxes => {
  if (newInboxes.length > 0 && pendingInboxIds.value.length > 0) {
    // Select the inboxes that were pending
    selectedInboxes.value = newInboxes.filter(inbox =>
      pendingInboxIds.value.includes(inbox.id)
    );

    // Clear the pending IDs after selection
    pendingInboxIds.value = [];

    debug(
      'Selected inboxes from pending IDs:',
      selectedInboxes.value.map(i => i.id)
    );
  }
});

// REMOVED: Watcher that was auto-updating selectedInboxes and causing interference with manual selection

// Watch for modals to lock/unlock scrolling
watch(loading, isLoading => {
  if (isLoading) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

watch(showRiskConfirmationModal, isShowing => {
  if (isShowing) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

watch(showBroadcastConfirmationModal, isShowing => {
  if (isShowing) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

watch(showInvalidNumbersModal, isShowing => {
  document.body.style.overflow = isShowing ? 'hidden' : '';
});

onMounted(async () => {
  store.dispatch('labels/get');
  loadContacts();

  try {
    const storeAccountId = store.getters['accounts/getCurrentAccountId'];

    if (storeAccountId) {
      accountId.value = storeAccountId;
    } else if (store.getters['auth/currentUser']) {
      accountId.value = store.getters['auth/currentUser'].account_id;
    }

    if (!accountId.value) {
      const currentPath = window.location.pathname;
      const accountMatch = currentPath.match(/accounts\/(\d+)/i);
      if (accountMatch && accountMatch[1]) {
        accountId.value = parseInt(accountMatch[1], 10);
      }
    }

    debug('Account ID obtido no onMounted:', accountId.value);
  } catch (err) {
    console.error('Erro ao obter account ID no onMounted:', err);
  }

  // Load inboxes first
  await loadInboxes();

  // Check for query parameters from retry
  if (route.query.recipients) {
    try {
      // Parse recipients from query
      const parsedRecipients = JSON.parse(route.query.recipients);

      // Populate recipients array - for retry, we want them selected by default
      if (Array.isArray(parsedRecipients)) {
        recipients.value = parsedRecipients.map(r => {
          const recipientId = Date.now() + Math.random();
          return {
            id: recipientId,
            name: r.name || '',
            phone: r.phone || r.phone_number || '',
            selectedInbox: r.inbox_id || null, // Map inbox_id to selectedInbox for consistency
            selected: true, // Select by default for retry
            customFields: r.customFields || {}, // Preserve custom fields from retry
            // Preserve any other additional fields from CSV import
            ...Object.keys(r).reduce((acc, key) => {
              if (
                ![
                  'id',
                  'name',
                  'phone',
                  'phone_number',
                  'inbox_id',
                  'selectedInbox',
                  'selected',
                  'customFields',
                ].includes(key)
              ) {
                acc[key] = r[key];
              }
              return acc;
            }, {}),
          };
        });

        // Also populate the selectedRecipientIds array
        selectedRecipientIds.value = recipients.value.map(r => r.id);

        // Extract unique inbox IDs
        const inboxIds = [
          ...new Set(
            parsedRecipients
              .map(r => r.inbox_id)
              .filter(id => id !== null && id !== undefined)
          ),
        ];

        // Store the inbox IDs to select later (after inboxes are loaded)
        pendingInboxIds.value = inboxIds;

        // Try to select if inboxes are already loaded
        if (inboxes.value.length > 0) {
          selectedInboxes.value = inboxes.value.filter(inbox =>
            inboxIds.includes(inbox.id)
          );
          pendingInboxIds.value = []; // Clear pending IDs after selection
        }
      }

      // Set message content if provided
      if (route.query.message) {
        message.value = route.query.message;
      }

      // Set template info if provided
      if (route.query.templateName && route.query.templateId) {
        selectedTemplate.value = {
          name: route.query.templateName,
          id: route.query.templateId,
        };
        isUsingTemplate.value = true;
      }

      // Show notification about retry
      if (route.query.broadcastId) {
        useAlert(
          `Reenvio do disparo #${route.query.broadcastId} - ${recipients.value.length} contatos selecionados`,
          'info'
        );
      }

      // Clear query params from URL to avoid confusion
      router.replace({
        name: route.name,
        params: route.params,
      });
    } catch (error) {
      console.error('Error parsing retry parameters:', error);
    }
  }

  // Add click outside handler for inbox dropdown
  const handleClickOutside = event => {
    if (!event.target.closest('[data-inbox-dropdown]')) {
      closeInboxDropdown();
    }
  };
  document.addEventListener('click', handleClickOutside);

  // Add document-level drag event listeners for full-page overlay
  document.addEventListener('dragenter', handleDocumentDragEnter);
  document.addEventListener('dragleave', handleDocumentDragLeave);
  document.addEventListener('dragover', handleDocumentDragOver);
  document.addEventListener('drop', handleDocumentDrop);
});

onBeforeUnmount(() => {
  // Ensure scrolling is restored if component unmounts while loading
  if (loading.value) {
    const mainContent = document.querySelector('.calendar-container');
    if (mainContent) {
      mainContent.style.overflow = '';
      mainContent.style.height = '';
      mainContent.style.position = '';
    }
  }

  // Restore body scroll if any modal is open
  if (
    loading.value ||
    showRiskConfirmationModal.value ||
    showBroadcastConfirmationModal.value
  ) {
    document.body.style.overflow = '';
  }

  // Clean up inbox dropdown scroll lock if active
  if (activeInboxDropdown.value) {
    document.body.style.overflow = '';
    document.removeEventListener('wheel', preventScroll);
    document.removeEventListener('touchmove', preventScroll);
  }

  // Clean up click outside handler
  const handleClickOutside = event => {
    if (!event.target.closest('[data-inbox-dropdown]')) {
      closeInboxDropdown();
    }
  };
  document.removeEventListener('click', handleClickOutside);

  // Clean up document-level drag event listeners
  document.removeEventListener('dragenter', handleDocumentDragEnter);
  document.removeEventListener('dragleave', handleDocumentDragLeave);
  document.removeEventListener('dragover', handleDocumentDragOver);
  document.removeEventListener('drop', handleDocumentDrop);
  // Reset drag state
  documentDragCounter.value = 0;
  isDraggingFile.value = false;
});
</script>

<template>
  <div
    class="flex flex-col h-full bg-n-background w-full overflow-hidden relative"
  >
    <!-- Header -->
    <header
      class="flex justify-between items-center h-14 min-h-[3.5rem] py-2 bg-n-background border-b border-n-weak px-4 md:pl-6"
    >
      <div class="flex items-center gap-2">
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <h1
          class="flex items-center mb-0 text-lg md:text-2xl text-slate-900 dark:text-slate-100"
        >
          Novo Disparo
        </h1>
      </div>
    </header>

    <!-- Componente de correção de toast -->
    <ToastFix />

    <!-- Área principal de conteúdo -->
    <div class="p-3 sm:p-4 md:p-5 bg-ash-50 flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative">
      <!-- Loading Inboxes Modal -->
      <div
        v-if="loading"
        class="absolute inset-0 z-50 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70 p-4"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-xl p-6 max-w-md w-full shadow-xl border border-n-slate-3 dark:border-n-slate-7"
        >
          <div class="flex items-center justify-center mb-4">
            <div
              class="w-16 h-16 bg-n-iris-2 dark:bg-n-slate-3 rounded-full flex items-center justify-center"
            >
              <div
                class="animate-spin w-8 h-8 border-4 border-n-slate-4 dark:border-n-slate-7 border-t-n-iris-9 dark:border-t-n-iris-11 rounded-full"
              />
            </div>
          </div>

          <h2
            class="text-xl font-semibold text-center mb-4 text-n-slate-12 dark:text-n-slate-12"
          >
            Carregando Caixas de Entrada
          </h2>
        </div>
      </div>
      <!-- Novo Disparo -->
      <div
        class="p-2 sm:p-4 md:p-6 max-w-full"
        :class="{
          'pointer-events-none opacity-60': loading,
        }"
      >
        <!-- Passo 1: Destinatários -->
        <div v-if="currentStep === 1" class="space-y-6">
          <!-- Info Card (oculto no mobile para economizar espaço) -->
          <div class="hidden sm:block shadow rounded-xl bg-n-solid-1 border-ash-700 p-4">
            <div class="flex flex-row justify-between items-start">
              <div>
                <h3
                  class="text-lg font-medium text-woot-600 dark:text-woot-600 mb-2 flex items-center gap-2"
                >
                  <i class="i-lucide-info"></i>
                  Destinatários
                </h3>
                <p class="text-sm text-n-slate-11 leading-relaxed max-w-full md:max-w-[80%]">
                  Adicione destinatários para o disparo em massa diretamente dos seus contatos, ou importe uma planilha. Você pode enviar para vários números diferentes.
                  <span class="font-bold">Os números de telefone serão utilizados exatamente como estiverem na planilha.</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Banner de Números Inválidos -->
          <div
            v-if="invalidRecipients.length > 0"
            class="mb-4 p-4 bg-gradient-to-r from-n-amber-2 to-n-amber-3 border border-n-amber-6 rounded-xl cursor-pointer hover:from-n-amber-3 hover:to-n-amber-4 transition-all duration-200"
            @click="showInvalidNumbersModal = true"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 bg-gradient-to-br from-n-amber-9 to-n-amber-10 rounded-lg flex items-center justify-center shadow-sm"
                >
                  <i class="i-lucide-alert-triangle text-white text-lg" />
                </div>
                <div>
                  <p class="text-base font-semibold text-n-amber-12">
                    {{ invalidRecipients.length }} número(s) não encontrado(s)
                    no WhatsApp
                  </p>
                  <p class="text-sm text-n-amber-11">
                    Clique para revisar e corrigir os números inválidos
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2 text-n-amber-11">
                <span class="text-sm font-medium">Revisar</span>
                <i class="i-lucide-chevron-right" />
              </div>
            </div>
          </div>

          <RecipientList
            v-model:selected-d-d-i="selectedDDI"
            :recipients="recipients"
            :inboxes="inboxes"
            :selected-inboxes="selectedInboxes"
            :selected-inbox-id="selectedInboxes[0]"
            :available-labels="allContactLabels"
            :additional-columns="additionalColumns"
            @import-csv="handleCSVImport"
            @add-from-label="addRecipientFromLabel"
            @remove="removeRecipient"
            @update-name="updateName"
            :chatwoot-contacts="allContacts"
            @update-phone="updatePhone"
            @update-email="updateEmail"
            :is-loading-contacts="isFetching"
            @update-inbox="updateInbox"
            :total-contacts-count="contactsMetaData.count"
            @update-multiple-inboxes="updateMultipleInboxes"
            @set-all-recipients-inbox="setAllRecipientsInbox"
            :current-contacts-page="contactsMetaData.currentPage"
            @update-random-inboxes="handleRandomInboxesUpdate"
            :total-contacts-pages="contactsMetaData.totalPages"
            @delete-selected="deleteSelectedRecipients"
            v-model:selected-ids="selectedRecipientIds"
            @load-contacts="loadContacts"
            @load-contacts-page="loadContactsPage"
            @update-custom-field="updateCustomField"
            @inbox-dropdown-toggle="handleInboxDropdownToggle"
          />

          <div class="flex justify-end">
            <button
              v-tooltip.top="nextButtonTooltip"
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
              :disabled="!canProceedToNext"
              @click="nextStep"
            >
              <span>Próximo</span>
              <span class="i-lucide-arrow-right" />
            </button>
          </div>
        </div>

        <!-- Passo 2: Mensagem -->
        <div v-else-if="currentStep === 2" class="space-y-6">
          <!-- Info Card (oculto no mobile para economizar espaço) -->
          <div class="hidden sm:block shadow rounded-xl bg-n-solid-1 border-ash-700 p-4">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
              <div>
                <h3
                  class="text-lg font-medium text-woot-600 dark:text-woot-600 mb-2 flex items-center gap-2"
                >
                  <i class="i-lucide-info"></i>
                  Criar mensagem
                </h3>
              </div>
              <div class="text-sm sm:text-base hover:text-woot-600 cursor-pointer transition-all flex items-center gap-2">
                Ver tutorial
                <i class="i-lucide-arrow-up-right"></i>
              </div>
            </div>
            <div class="text-sm text-n-slate-11 leading-relaxed max-w-full md:max-w-[80%] flex flex-col">
              <span>
                Você pode utilizar variáveis, para personalizar a mensagem para
                cada destinatário. O limite é de um anexo por mensagem, com
                tamanho máximo de 20 MB.
                Variações não selecionadas não serão enviadas.
              </span>
              <span v-if="isUsingWhatsappCloud">
                <span class="font-bold">
                  Só é possível fazer disparos com Whatsapp Oficial usando templates.
                </span>
                Você pode criar templates no painel do WhatsApp Business API e usá-los aqui.
              </span>
            </div>
          </div>

          <!-- Template Selection -->
          <div v-if="showTemplateSelection" class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <h3
                class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
              >
                Templates
              </h3>
            </div>

            <!-- Template selection interface ou aviso -->
            <div v-if="isUsingTemplate" class="mt-4">
              <MessageTemplate
                ref="messageTemplateRef"
                :selected-template="selectedTemplate"
                :selected-inbox-id="getFirstSelectedInboxId()"
                :additional-columns="additionalTemplateColumns"
                :recipients="recipients"
                @select-template="selectTemplate"
              />
            </div>
            <div
              v-else
              class="mt-4 bg-n-amber-1 dark:bg-n-amber-2 border border-n-amber-4 dark:border-n-amber-6 rounded-lg p-4"
            >
              <div class="flex items-start">
                <i
                  class="i-lucide-alert-triangle text-n-amber-11 dark:text-n-amber-11 text-xl mr-3 mt-0.5"
                />
                <div>
                  <p class="text-n-amber-11 dark:text-n-amber-11">
                    Templates criados podem demorar até 24 horas para ser
                    aprovados
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Message Composer (Disponível para API não oficial) -->
          <div
            v-if="
              !isUsingWhatsappCloud ||
              (!isUsingTemplate && !showTemplateSelection)
            "
            class="mt-4"
          >

          <!-- Layout: editor e preview lado a lado (empilha em telas menores) -->
          <div class="flex flex-col lg:flex-row gap-6">
              <!-- Editor de mensagem e configurações avançadas -->
              <div class="space-y-6 flex-1 min-w-0">
                <!-- Editor de mensagem -->
                <div>
                  <h3
                    class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12 mb-4"
                  >
                    Escreva sua mensagem
                  </h3>
                  <MessageComposer
                    ref="messageComposerRef"
                    v-model="message"
                    :attachments="attachments"
                    :preview="false"
                    :recipients="recipients"
                    :hide-preview-button="true"
                    :additional-columns="additionalTemplateColumns"
                    :initial-variations-state="messageComposerVariationsState"
                    :is-dragging-files="isDraggingFile"
                    :has-messenger-inbox="hasMessengerInbox"
                    @file-upload="processFileUpload"
                    @remove-attachment="index => attachments.splice(index, 1)"
                    @toggle-preview="messagePreview = !messagePreview"
                    @variations-confirmed="handleVariationsConfirmed"
                  />
                </div>

                <!-- Interval Control - Conditional based on API type -->
                <div>
                  <h3
                    class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12 mb-4"
                  >
                    Controle de Intervalo
                  </h3>
                  <div class="shadow rounded-xl bg-n-solid-1 border-ash-700 p-4">
                    <div class="space-y-4">
                    <!-- Speed Control Info -->
                    <div class="mb-4">
                      <p class="text-sm text-n-slate-11 leading-relaxed">
                        Configure o intervalo entre cada envio de mensagem.
                      </p>
                    </div>

                    <!-- Dual Handle Range Slider -->
                    <div class="space-y-4">
                      <!-- Values Display -->
                      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <label class="text-sm font-medium text-n-slate-12">
                          Intervalo de Tempo
                        </label>
                        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <div class="flex items-center gap-1">
                            <span class="text-sm text-n-slate-11">Mín:</span>
                            <button
                              v-if="!editingMin"
                              @click="startEditingMin"
                              class="text-lg font-semibold text-n-slate-12 hover:text-woot-600 cursor-pointer transition-colors relative group"
                            >
                              {{ minDelay }}s
                              <span class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-n-slate-12 dark:bg-n-slate-1 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                                Clique para editar
                              </span>
                            </button>
                            <input
                              v-else
                              ref="minDelayInput"
                              v-model.number="tempMinDelay"
                              @blur="finishEditingMin"
                              @keyup.enter="finishEditingMin"
                              @keyup.escape="cancelEditingMin"
                              type="number"
                              min="1"
                              max="285"
                              class="w-16 px-1 text-lg font-semibold text-n-slate-12 bg-n-slate-1 border border-n-iris-6 rounded focus:outline-none focus:ring-1 focus:ring-n-iris-9"
                            />
                          </div>
                          <span class="text-n-slate-11">—</span>
                          <div class="flex items-center gap-1">
                            <span class="text-sm text-n-slate-11">Máx:</span>
                            <button
                              v-if="!editingMax"
                              @click="startEditingMax"
                              class="text-lg font-semibold text-n-slate-12 hover:text-woot-600 cursor-pointer transition-colors relative group"
                            >
                              {{ maxDelay }}s
                              <span class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-n-slate-12 dark:bg-n-slate-1 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                                Clique para editar
                              </span>
                            </button>
                            <input
                              v-else
                              ref="maxDelayInput"
                              v-model.number="tempMaxDelay"
                              @blur="finishEditingMax"
                              @keyup.enter="finishEditingMax"
                              @keyup.escape="cancelEditingMax"
                              type="number"
                              min="16"
                              max="300"
                              class="w-16 px-1 text-lg font-semibold text-n-slate-12 bg-n-slate-1 border border-n-iris-6 rounded focus:outline-none focus:ring-1 focus:ring-n-iris-9"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Dual Range Slider Container -->
                      <div class="relative pt-4 pb-3">
                        <!-- Track Background -->
                        <div class="w-full h-2 bg-n-slate-3 dark:bg-n-slate-7 rounded-lg absolute" style="top: 4px"></div>
                        
                        <!-- Active Range Fill -->
                        <div 
                          class="h-2 bg-n-iris-9 dark:bg-n-iris-10 rounded-lg absolute"
                          style="top: 4px"
                          :style="{
                            left: `${(minDelay - 1) / 299 * 100}%`,
                            width: `${((maxDelay - minDelay) / 299) * 100}%`,
                            top: '4px'
                          }"
                        ></div>

                        <!-- Min Range Input -->
                        <input
                          v-model.number="minDelay"
                          type="range"
                          min="1"
                          max="300"
                          class="dual-range-slider absolute w-full"
                          style="top: 0"
                          @input="() => { if (minDelay > maxDelay - 15) minDelay = maxDelay - 15 }"
                        />

                        <!-- Max Range Input -->
                        <input
                          v-model.number="maxDelay"
                          type="range"
                          min="1"
                          max="300"
                          class="dual-range-slider absolute w-full"
                          style="top: 0"
                          @input="() => { if (maxDelay < minDelay + 15) maxDelay = minDelay + 15 }"
                        />

                        <!-- Scale Marks with proper positioning -->
                        <div class="flex justify-between mt-4 text-[10px] sm:text-xs text-n-slate-11">
                          <span>1s</span>
                          <span>60s</span>
                          <span>120s</span>
                          <span>180s</span>
                          <span>240s</span>
                          <span>300s</span>
                        </div>
                      </div>
                    </div>

                    <!-- Current Range Display with Dynamic Warning/Info State -->
                    <div
                      :class="(minDelay < 15 || maxDelay < 60) 
                        ? 'bg-n-ruby-2 border border-n-ruby-6' 
                        : 'bg-n-iris-2 border border-n-iris-6'"
                      class="rounded-lg p-2"
                    >
                      <div class="flex items-start gap-2">
                        <i 
                          :class="(minDelay < 15 || maxDelay < 60)
                            ? 'i-lucide-alert-triangle text-n-ruby-9'
                            : 'i-lucide-info text-n-iris-9'"
                          class="text-base mt-0.5" 
                        />
                        <div>
                          <p 
                            :class="(minDelay < 15 || maxDelay < 60)
                              ? 'text-n-ruby-11'
                              : 'text-n-iris-11'"
                            class="text-sm"
                          >
                            O sistema escolherá um tempo aleatório entre
                            <strong>{{ minDelay }}</strong> e
                            <strong>{{ maxDelay }}</strong> segundos para cada
                            envio.
                          </p>
                          <p
                            v-if="minDelay < 15 || maxDelay < 60"
                            class="text-sm text-n-ruby-11 mt-1"
                          >
                            Intervalos menores de 1 minuto podem aumentar os riscos de bloqueio no WhatsApp.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              <div class="flex-1 min-w-0">
                <!-- Preview do WhatsApp -->
                <h3
                  class="text-lg font-medium text-n-slate-12 dark:text-slate-200 mb-4"
                >
                  Prévia da mensagem
                </h3>
                <div class="lg:sticky lg:top-0">
                  <MessagePreview
                    type="whatsapp"
                    :message="processedMessage"
                    :recipient-name="
                      recipients.length > 0 && recipients[0].name
                        ? recipients[0].name
                        : 'Nome do Cliente'
                    "
                    :attachments="attachments"
                    :recipients="recipients"
                    :additional-columns="additionalTemplateColumns"
                  />
                </div>
              </div>
            </div>
          </div>

        <!-- Send/Back buttons - Now outside conditional divs, visible for both templates and regular messages -->
        <div class="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 mt-10">
          <button
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center w-full sm:w-auto"
            @click="prevStep"
          >
            <span class="i-lucide-arrow-left" />
            <span>Voltar</span>
          </button>

          <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-2 rounded-lg outline-1 outline disabled:opacity-50 bg-transparent border-n-iris-6 text-n-iris-11 hover:enabled:bg-n-iris-2 focus-visible:bg-n-iris-2 outline-transparent h-10 px-4 text-sm font-medium justify-center w-full sm:w-auto"
              :disabled="!canScheduleBroadcast || isSubmitting"
              @click="showScheduleBroadcastModal"
            >
              <span class="i-lucide-clock" />
              <span>Agendar disparo</span>
            </button>

            <button
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center w-full sm:w-auto"
              :disabled="!canSend || isSubmitting"
              @click="showConfirmationModal"
            >
              <span v-if="isSubmitting" class="animate-spin i-lucide-loader" />
              <span v-else class="i-lucide-send" />
              <span>{{
                isSubmitting ? 'Enviando...' : 'Enviar mensagens'
              }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Card com resumo das informações (compacto no mobile) -->
      <div
        class="mt-4 sm:mt-6 p-3 sm:p-4 bg-n-solid-1 rounded-md border border-n-weak dark:border-none"
      >
        <!-- Mobile: linha única compacta com chips -->
        <div class="flex sm:hidden flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span class="inline-flex items-center gap-1 text-n-slate-11">
            <i class="i-lucide-users text-sm" />
            <span
              class="font-semibold"
              :class="
                recipients.length > 2000
                  ? 'text-n-ruby-11'
                  : 'text-n-slate-12'
              "
              >{{ recipients.length || 0 }}<span v-if="recipients.length > 0" class="font-normal text-n-slate-11">/2000</span></span
            >
          </span>
          <span class="text-n-slate-6">·</span>
          <span class="inline-flex items-center gap-1 text-n-slate-11">
            <i class="i-lucide-inbox text-sm" />
            <span class="font-semibold text-n-slate-12">{{ uniqueInboxesInUse.length || 0 }}</span>
          </span>
          <span class="text-n-slate-6">·</span>
          <span class="inline-flex items-center gap-1 text-n-slate-11 min-w-0">
            <i class="i-lucide-file-text text-sm flex-shrink-0" />
            <span class="font-semibold text-n-slate-12 truncate">{{ messageTypeDisplay || '-' }}</span>
          </span>
          <span class="text-n-slate-6">·</span>
          <span
            class="inline-flex items-center gap-1 font-semibold min-w-0"
            :class="canSend ? 'text-n-grass-11' : 'text-n-slate-12'"
          >
            <i
              :class="canSend ? 'i-lucide-check-circle' : 'i-lucide-circle-dashed'"
              class="text-sm flex-shrink-0"
            />
            <span class="truncate">{{ canSend ? 'Pronto' : 'Incompleto' }}</span>
          </span>
        </div>

        <!-- Tablet/Desktop: grid completo -->
        <div class="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">
          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11"
              >Destinatários</span
            >
            <span
              class="text-lg md:text-xl font-medium truncate"
              :class="
                recipients.length > 2000
                  ? 'text-n-ruby-11 dark:text-n-ruby-11'
                  : 'text-n-slate-12 dark:text-n-slate-12'
              "
            >
              {{ recipients.length || 0 }}
              <span v-if="recipients.length > 0" class="text-sm font-normal">/ 2000</span>
            </span>
          </div>

          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11"
              >Inboxes selecionadas</span
            >
            <span
              class="text-lg md:text-xl font-medium text-n-slate-12 dark:text-n-slate-12 truncate"
              >{{ uniqueInboxesInUse.length || 0 }}</span
            >
          </div>

          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11"
              >Conteúdo</span
            >
            <span
              class="text-lg md:text-xl font-medium text-n-slate-12 dark:text-n-slate-12 truncate"
            >
              {{ messageTypeDisplay || '-' }}
            </span>
          </div>

          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11"
              >Status</span
            >
            <span
              class="text-lg md:text-xl font-medium truncate"
              :class="
                canSend
                  ? 'text-n-grass-11 dark:text-n-grass-11'
                  : 'text-n-slate-12'
              "
            >
              {{ canSend ? 'Pronto para envio' : 'Configuração incompleta' }}
            </span>
          </div>
        </div>
      </div>

        <!-- Overlay de Progresso para Envio em Lotes (Dialog mais elegante) -->
        <BroadcastProgressOverlay
          :is-visible="showProgressOverlay"
          :total-recipients="sendingProgress.totalRecipients"
          :current-progress="sendingProgress.currentProgress"
          :success-count="sendingProgress.successCount"
          :failed-count="sendingProgress.failedCount"
          :errors="sendingProgress.errors"
          :broadcast-id="sendingProgress.broadcastId"
          :is-cancellable="!isCancelling"
          :current-batch="currentBatchIndex"
          :total-batches="totalBatches"
          :is-processing-batch="isProcessingBatch"
          :is-cancelling="isCancelling"
          @cancel="handleCancelBroadcast"
          @close="closeProgressOverlay"
        />

        <!-- Lista de erros detalhada (Melhor formatação) -->
        <!-- <div
        v-if="sendingProgress.errors.length"
        class="p-4 max-w-5xl mx-auto"
      >
        <div
          class="bg-n-ruby-1 dark:bg-n-ruby-2 rounded-lg p-5 border border-n-ruby-4 dark:border-n-ruby-6"
        >
          <h3
            class="text-n-ruby-11 dark:text-n-ruby-11 font-semibold flex items-center mb-3"
          >
            <i class="i-lucide-alert-triangle mr-2" />
            Falhas nos envios ({{ sendingProgress.errors.length }})
          </h3>
          <div class="max-h-[300px] overflow-y-auto">
            <ul
              class="list-disc list-inside text-sm text-n-ruby-11 dark:text-n-ruby-11 space-y-2"
            >
              <li
                v-for="(e, i) in sendingProgress.errors"
                :key="i"
                class="py-1 border-b border-n-ruby-3 dark:border-n-ruby-6 last:border-0"
              >
                <strong>{{ e.phone }}</strong> — {{ e.error }}
              </li>
            </ul>
          </div>
        </div>
      </div> -->
      </div>

      <!-- Mobile Template Validation Modal -->
      <MobileTemplateValidationModal
        :show="showMobileValidationModal"
        :template="selectedTemplate"
        :is-submitting="isSubmitting"
        :recipient-name="
          recipients.length > 0 && recipients[0].name
            ? recipients[0].name
            : 'João Silva'
        "
        :attachments="attachments"
        :parameter-errors="mobileParameterErrors"
        @close="showMobileValidationModal = false"
        @confirm="confirmMobileSend"
        @update-template="selectedTemplate = $event"
      />

      <!-- Risk Confirmation Modal -->
      <div
        v-if="showRiskConfirmationModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70 p-4"
      >
        <div
          class="bg-white dark:bg-n-slate-2 rounded-xl w-full max-w-xl shadow-xl border border-n-slate-3 dark:border-n-slate-6"
        >
          <!-- Modal Header -->
          <div
            class="flex items-center justify-between p-6 border-b border-n-slate-3 dark:border-n-slate-6"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-n-ruby-2 dark:bg-n-ruby-3 rounded-lg flex items-center justify-center"
              >
                <i
                  class="i-lucide-alert-triangle text-n-ruby-9 dark:text-n-ruby-11 text-xl"
                />
              </div>
              <h3
                class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
              >
                Confirmar Intervalos Baixos
              </h3>
            </div>
            <button
              class="text-n-slate-9 hover:text-n-slate-11 dark:text-n-slate-11 dark:hover:text-n-slate-12 transition-colors"
              @click="showRiskConfirmationModal = false"
            >
              <i class="i-lucide-x text-xl" />
            </button>
          </div>

          <!-- Modal Content -->
          <div class="p-6">
            <!-- Warning Message -->
            <div
              class="bg-n-ruby-2 dark:bg-n-ruby-3 border border-n-ruby-6 dark:border-n-ruby-7 rounded-lg p-4 mb-6"
            >
              <div class="flex items-start gap-3">
                <i
                  class="i-lucide-alert-triangle text-n-ruby-9 dark:text-n-ruby-11 text-xl mt-0.5 flex-shrink-0"
                />
                <div>
                  <h4
                    class="font-semibold text-n-ruby-11 dark:text-n-ruby-11 mb-2"
                  >
                    Atenção: Intervalos muito baixos
                  </h4>
                  <p
                    class="text-sm text-n-ruby-11 dark:text-n-ruby-11 leading-relaxed"
                  >
                    Intervalos menores que 15-60 segundos podem aumentar
                    significativamente os riscos de sua conta no WhatsApp ser
                    banida ou suspensa.
                  </p>
                </div>
              </div>
            </div>

            <!-- Current Settings -->
            <div class="bg-n-slate-1 dark:bg-n-slate-3 rounded-lg p-4 mb-6">
              <h5 class="font-medium text-n-slate-12 dark:text-n-slate-12 mb-3">
                Configuração Atual:
              </h5>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-n-slate-11 dark:text-n-slate-11"
                    >Tempo mínimo:</span
                  >
                  <span
                    class="font-semibold text-n-slate-12 dark:text-n-slate-12 ml-2"
                    >{{ minDelay }}s</span
                  >
                </div>
                <div>
                  <span class="text-n-slate-11 dark:text-n-slate-11"
                    >Tempo máximo:</span
                  >
                  <span
                    class="font-semibold text-n-slate-12 dark:text-n-slate-12 ml-2"
                    >{{ maxDelay }}s</span
                  >
                </div>
              </div>
            </div>

            <!-- Confirmation Text -->
            <div class="text-center mb-6">
              <p
                class="text-n-slate-11 dark:text-n-slate-11 text-sm leading-relaxed"
              >
                Você tem certeza que deseja continuar com estes intervalos?
                <strong class="text-n-slate-12 dark:text-n-slate-12"
                  >Você assume todos os riscos</strong
                >
                relacionados ao uso de intervalos baixos.
              </p>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-3 bg-n-slate-2 hover:bg-n-slate-3 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 text-n-slate-12 dark:text-n-slate-12 rounded-lg font-medium transition-colors duration-200"
                @click="showRiskConfirmationModal = false"
              >
                Cancelar
              </button>
              <button
                class="flex-1 px-4 py-3 bg-n-ruby-9 hover:bg-n-ruby-10 dark:bg-n-ruby-9 dark:hover:bg-n-ruby-10 text-white rounded-lg font-medium transition-colors duration-200"
                @click="confirmRiskySend"
              >
                Continuar Mesmo Assim
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Values Validation Modal -->
      <EmptyValuesValidationModal
        :show="showEmptyValuesModal"
        :empty-fields-data="emptyFieldsValidationData"
        @close="handleEmptyValuesModalClose"
        @go-back="handleEmptyValuesGoBack"
        @continue="handleEmptyValuesContinue"
      />

      <!-- Broadcast Confirmation Modal -->
      <BroadcastConfirmationModal
        :show="showBroadcastConfirmationModal"
        :recipient-count="recipients.length"
        :inbox-count="uniqueInboxesInUse.length"
        :message-type="messageTypeDisplay"
        :estimated-time="estimatedBroadcastTime"
        :available-labels="allContactLabels"
        :is-confirming="isSubmitting"
        :disconnected-inbox-warnings="disconnectedInboxWarning"
        @close="showBroadcastConfirmationModal = false"
        @confirm="handleBroadcastConfirmation"
        @create-label="handleCreateLabel"
        @show-add-label-modal="showAddLabelModal"
      />

    <!-- Broadcast Schedule Modal -->
    <BroadcastScheduleModal
      :show="showScheduleModal"
      :recipient-count="recipients.length"
      :inbox-count="uniqueInboxesInUse.length"
      :available-labels="allContactLabels"
      @close="showScheduleModal = false"
      @confirm="handleScheduleBroadcast"
      @create-label="handleCreateLabel"
      @show-add-label-modal="showAddLabelModal"
    />

    <!-- Inbox Selection Modal -->
    <InboxSelectionModal
      :show="showInboxSelectionModal"
      :inboxes="connectedInboxes"
      :loading="loading"
      @select="handleDefaultInboxSelection"
    />

    <!-- Invalid Numbers Modal -->
    <InvalidNumbersModal
      ref="invalidNumbersModalRef"
      :show="showInvalidNumbersModal"
      :invalid-numbers="invalidRecipients"
      :is-revalidating="isValidatingNumbers"
      @close="showInvalidNumbersModal = false"
      @revalidate="handleInvalidNumbersRevalidation"
      @remove-all="handleRemoveAllInvalid"
    />

    <!-- Inbox Dropdown (positioned at page level to avoid clipping) -->
    <div
      v-if="activeInboxDropdown"
      class="fixed z-50 border border-n-slate-6 dark:border-n-slate-7 rounded-lg shadow-lg bg-n-solid-1 dark:bg-n-slate-2 overflow-hidden min-w-[250px] max-w-[calc(100vw-1rem)]"
      :style="{
        left: dropdownPosition.left + 'px',
        top: dropdownPosition.top !== undefined ? dropdownPosition.top + 'px' : undefined,
        bottom: dropdownPosition.bottom !== undefined ? dropdownPosition.bottom + 'px' : undefined
      }"
      data-inbox-dropdown
    >
      <div class="max-h-60 overflow-y-auto">
        <template
          v-for="(item, index) in groupedInboxOptions"
          :key="index"
        >
          <!-- Group Header -->
          <div
            v-if="item.type === 'header'"
            class="px-3 py-1 text-xs font-semibold bg-n-slate-1 text-n-slate-11 dark:text-n-slate-11 border-y border-n-slate-3 dark:border-n-slate-6 sticky top-0 z-10"
          >
            {{ item.label }}
          </div>
          
          <!-- Inbox Option -->
          <div
            v-else
            class="p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-4 cursor-pointer"
            @click="handleInboxSelection(activeInboxDropdown, item.id)"
          >
            <div class="flex items-center">
              <div
                class="w-8 h-8 flex items-center justify-center rounded-full mr-3"
                :class="{
                  'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11': item.integration_type === 'evolution',
                  'bg-grass-300 text-grass-800 dark:bg-grass-200': item.integration_type === 'official',
                  'bg-n-slate-2 text-n-slate-11 dark:bg-n-slate-3 dark:text-n-slate-11': !item.integration_type
                }"
              >
                <i :class="getInboxTypeIcon(item.channel_type)" />
              </div>
              <div>
                <div class="text-base font-medium">
                  {{ item.display_name || item.inbox_name || item.name }}
                </div>
                <div
                  v-if="item.integration_type === 'official'"
                  class="text-sm text-n-grass-11 dark:text-n-grass-11"
                >
                  API Oficial
                </div>
                <div
                  v-else-if="item.integration_type === 'evolution'"
                  class="text-sm text-n-amber-11 dark:text-n-amber-11"
                >
                  Não Oficial
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  </div>
</template>

<style>
/* Copy the same styles from the original BulkMessage.vue */
:root {
  --primary-color: #2b70e0ff;
  --primary-hover: #3b7eeb;
  --light-primary: #f8f8f8ff;
  --light-primary-hover: #f0f0f3ff;
  --white: #ffffffff;
  --black: #0c0d11ff;
  --bg: #fdfdfdff;
  --light-gray: #f0f0f3ff;
  --text-gray: #5e5e5eff;
  --danger-color: #b31a30ff;
  --success-color: #1cb954;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
  --border-radius: 8px;
  --transition-speed: 0.2s;
}

.header-wrapper {
  z-index: 10;
  background-color: var(--white);
  color: var(--black);
  padding: 1rem 0;
  box-shadow: var(--shadow-sm);
  border-bottom: 1px solid var(--light-gray);
}

:global(.dark) .header-wrapper {
  background-color: #1e293b;
  color: #e2e8f0;
  border-bottom-color: #334155;
}

/* Clean structure following agentis pattern */

/* Responsividade melhorada */
@media (max-width: 640px) {
  /* Reduce button sizes on mobile */
  button {
    padding: 0.5rem 1rem;
  }

  /* Stack buttons vertically on very small screens */
  .flex-row > button {
    width: 100%;
  }
}

@media (max-width: 768px) {
  /* Tablet adjustments */
  .message-preview {
    display: none;
  }

  /* Reduce modal sizes */
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}

@media (min-width: 1024px) {
  .message-composer-container {
    max-width: 100%;
  }
}
</style>
